/**
 * Executing a conversion.
 *
 * Everything is written in a single pass over the data records. All rate groups are
 * open at once and fed from the same batch of bytes, so a file is read once no
 * matter how many output tables it produces, and memory stays flat.
 */

import { createWriteStream, fstatSync } from 'node:fs';
import { lstat, mkdir, readdir, stat, writeFile } from 'node:fs/promises';
import { finished } from 'node:stream/promises';
import type { Writable } from 'node:stream';
import { createGzip, gzipSync } from 'node:zlib';
import path from 'node:path';

// A destination is whatever the caller's shell handed over; see printable's own comment.
import { printable } from '../format/unprintable.js';

import type { RecordBatch } from '../edf/reader.js';
import { EdfFile } from '../edf/reader.js';
import { describeFormat, formatRate, formatRates, formatWallClock, startsFormula } from '../edf/header.js';
import type { Diagnostic } from '../edf/errors.js';
import { EdfError } from '../edf/errors.js';
import type { Annotation } from '../edf/annotations.js';
import {
  BufferedLineWriter,
  DEFAULT_FLUSH_THRESHOLD,
  UTF8_BOM,
  csvRow,
  escapeCsvField,
} from '../format/csv.js';
import { counted, grouped, listed } from '../format/list.js';
import { escapeCharacter, escapeJsonText, unprintableIn } from '../format/unprintable.js';
import {
  makeSampleFormatter,
  makeTimeFormatter,
  newOffsetBudget,
  newSampleCacheBudget,
  plain,
} from '../format/number.js';
import type { SampleFormatter } from '../format/number.js';
import { TIME_COLUMN } from './channels.js';
import { assertInputPath, assertOptions } from './options.js';
import { buildPlan, outputCsvName, withoutFileRateWarning } from './plan.js';
import type { ConversionPlan, PlanOptions, RateGroup } from './plan.js';
import { deriveRecordStarts, withTimingPromiseKept } from './timing.js';
import { sampleTimeIsInRange, toleranceFor } from './time-range.js';
import { VERSION as TOOL_VERSION } from '../version.js';

export { TOOL_VERSION };

export type ConversionErrorCode =
  | 'OUTPUT_EXISTS'
  | 'OUTPUT_UNWRITABLE'
  | 'INPUT_OUTPUT_COLLISION'
  | 'INPUT_UNREADABLE'
  | 'UNSUPPORTED_REQUEST'
  | 'CALLBACK_FAILED'
  | 'WRITE_FAILED';

/**
 * Codes that mean the command cannot be carried out as written, rather than that something
 * about the file or the destination went wrong.
 *
 * The distinction is the one the exit codes draw: 1 is "the file or the destination is the
 * problem", 2 is "the command line is the problem". A caller with a `--stdout` conflict is
 * being told to change the flags — the hints say exactly that — so filing it under 1 sent
 * scripts looking at the disk. Exit 2 already covers checks that need the header first,
 * such as a `--channels` term matching nothing.
 */
export const USAGE_ERROR_CODES: ReadonlySet<ConversionErrorCode> = new Set(['UNSUPPORTED_REQUEST']);

export class ConversionError extends Error {
  readonly code: ConversionErrorCode;
  readonly hint: string | undefined;
  constructor(code: ConversionErrorCode, message: string, hint?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'ConversionError';
    this.code = code;
    this.hint = hint;
  }
}

export interface ConvertOptions extends PlanOptions {
  /**
   * Destination directory. Defaults to `defaultOutputDir(inputPath)`: the input's name with
   * its extension replaced by `_csv`, beside the input — `sleep-study.edf` gives
   * `sleep-study_csv`, not `sleep-study`.
   */
  outputDir?: string | undefined;
  /** Overwrite an existing output directory. */
  force?: boolean | undefined;
  /** Record a SHA-256 of the input in metadata.json. Costs one extra read of the file. */
  checksum?: boolean | undefined;
  /**
   * Write the signal CSV to stdout instead of to a directory.
   *
   * Only valid when the conversion produces exactly one signal file. In the default wide
   * layout a mixed-rate recording becomes several tables, and merging them into one stream
   * would mean inventing the samples this tool exists not to invent; `layout: 'long'` gives
   * one table for any recording, so it lifts the restriction. No sidecar files are written.
   */
  toStdout?: boolean | undefined;
  onProgress?: ((progress: ConversionProgress) => void) | undefined;
}

export interface ConversionProgress {
  recordsDone: number;
  recordsTotal: number;
  bytesWritten: number;
}

export interface WrittenFile {
  name: string;
  rows: number;
}

export interface ConvertResult {
  outputDir: string;
  files: WrittenFile[];
  /**
   * True when a `--stdout` reader closed the pipe before the conversion finished.
   *
   * `edf2csv rec.edf --stdout | head -1` is an ordinary thing to type and not a failure, but
   * it is also not a conversion: the row count is rows formatted before the close was
   * noticed, which is neither the recording's total nor what the reader received.
   */
  readerHungUp: boolean;
  annotationCount: number;
  diagnostics: Diagnostic[];
  plan: ConversionPlan;
  file: EdfFile;
  elapsedMs: number;
}

interface OpenGroup {
  group: RateGroup;
  writer: BufferedLineWriter;
  formatters: SampleFormatter[];
  formatTime: (recordStart: number, sample: number) => string;
  rows: number;
  /** Resolves once a compressed stream's bytes have reached the file behind it. */
  settled: Promise<void>;
  /** Detaches the compressor's error forwarding from a stream this tool does not own. */
  release: () => void;
}

export async function convert(inputPath: string, options: ConvertOptions = {}): Promise<ConvertResult> {
  assertInputPath(inputPath);
  // And the bag behind it, here rather than at `buildPlan` below: `options.checksum` is read
  // before the plan is built, so a bag that is not one reached that line first. See
  // assertOptions for what a string in this position did.
  assertOptions(options);
  const startedAt = Date.now();
  const file = await EdfFile.open(inputPath);

  try {
    /*
      Hashed before a record is read, and only published if the file held still.

      A checksum taken afterwards cannot be trusted whichever descriptor it goes through. A
      file overwritten in place keeps its inode, so the open handle sees the new bytes too,
      and the old ones are simply gone — there is nowhere left to read what was converted.
      Taking it first at least means the hash describes the file the header was read from.

      What makes it a guarantee rather than a hope is the check at the end: if size or
      modification time moved at any point, the hash is dropped and the run says why. So
      `sha256` present means the file demonstrably did not change while it was read, and a
      recording still being written gets a null and a warning instead of a plausible hash of
      the wrong bytes. (A change reverted within the same modification timestamp would slip
      through; nothing short of copying the input first can close that.)
    */
    const checksumAtOpen = options.checksum === true ? await file.sha256() : null;

    // One pass over the annotation channel supplies everything annotation-related:
    // where each record sits in time, and the full event list. It reads the whole
    // file even when a window was requested, because an annotation inside the window
    // may be stored in a record outside it.
    const annotationData =
      file.annotationSignals.length > 0
        ? await file.readAnnotations()
        : { annotations: [] as Annotation[], recordStarts: [] as (number | null)[], malformed: 0 };

    const timing = deriveRecordStarts(file, annotationData);

    const plan = buildPlan(
      {
        signals: file.header.signals,
        recordDuration: file.header.recordDuration,
        recordCount: file.recordCount,
        hasAnnotationChannel: file.annotationSignals.length > 0,
        recordStarts: timing.starts,
      },
      options,
    );
    plan.diagnostics.push(...timing.diagnostics);

    if (options.toStdout === true) {
      const refusal = stdoutRefusal(file, plan);
      if (refusal) throw refusal;
      let readerHungUp = false;
      const written = await writeSignalFiles(file, plan, null, timing.starts, options, (hungUp) => {
        readerHungUp = hungUp;
      });
      if (await file.changedSinceOpen()) plan.diagnostics.push(inputChanged(false));
      return {
        outputDir: '-',
        files: written,
        readerHungUp,
        annotationCount: 0,
        // Over both lists, because the timing warnings are pushed onto the plan's.
        diagnostics: withSidecarsNamed(
          withSignalTableUnwritten(
            [
              ...withTimingPromiseKept(withoutFileRateWarning(file.diagnostics), timing.starts !== null),
              ...plan.diagnostics,
            ],
            plan.writeSignals,
            plan.gzip,
            convertedChannels(plan),
            true,
            plan.layout === 'long',
            channelsWithoutRows(plan),
          ),
          { toStdout: true, gzip: plan.gzip },
        ),
        plan,
        file,
        elapsedMs: Date.now() - startedAt,
      };
    }

    /*
      A destination whose last component is `.` or `..` does not name a directory of its own.

      `prepareOutputDir` claims the final component with a single non-recursive mkdir, having
      created its parents recursively — which is what makes two conversions racing for one
      directory safe. `path.dirname("out/.")` is `"out"`, so for these the parent step creates
      the destination itself and the claim then asks the filesystem to make `.` inside it,
      which always exists. The result was a refusal naming a directory this same run had just
      made, one line after making it:

          edf2csv rec.edf --out ./fresh/.
          error: "./fresh/." already exists.
                 Pass --force to overwrite it, or --out to choose a different directory.

      Exit 1, nothing converted, and an empty `fresh/` left on disk. `--force` does not help:
      the claim fails the same way whatever it is told, so the path was unusable rather than
      occupied.

      Normalised only in that case, so `--out ./converted` keeps the spelling it was given —
      that one is already how the directory is found on disk, which is what `output_dir`
      promises, and rewriting it to `converted` would churn every example for no gain.
    */
    const asked = options.outputDir ?? defaultOutputDir(inputPath);
    const outputDir = /(?:^|[\\/])\.\.?$/u.test(asked) ? path.normalize(asked) : asked;
    await assertInputDoesNotOverlapOutputs(inputPath, outputDir, file, plan, options);
    await prepareOutputDir(outputDir, options.force === true);

    const written: WrittenFile[] = [];

    if (plan.writeSignals && plan.groups.length > 0) {
      const signals = await writeSignalFiles(file, plan, outputDir, timing.starts, options);
      written.push(...signals);
    } else {
      const missing = noSignalFile(file, plan);
      if (missing) plan.diagnostics.push(missing);
    }

    const noEvents = noAnnotations(file, options);
    if (noEvents) plan.diagnostics.push(noEvents);

    let annotationsWritten = 0;
    if (file.annotationSignals.length > 0) {
      const window = requestedAnnotationWindow(options, plan.range.recordingStartSeconds);
      // Reported against the rows that will be written, not against the file; see
      // durationDiagnostics.
      plan.diagnostics.push(
        ...durationDiagnostics(annotationData.annotations, window, plan.gzip),
      );
      plan.diagnostics.push(
        ...descriptionDiagnostics(annotationData.annotations, window, plan.gzip),
      );
      const result = await writeAnnotationsCsv(
        outputDir,
        annotationData.annotations,
        window,
        options.gzip === true,
        options.bom === true,
      );
      written.push(result);
      annotationsWritten = result.rows;
      /*
        A run whose only table came out empty, which nothing said.

        `emptyWindow`'s own docstring states the rule this was breaking: "everywhere else that
        a request produces nothing this tool says so: a --channels term matching nothing is an
        error, and --annotations-only on a file with no events raises NO_ANNOTATIONS." The
        second half is true only of a file with no annotation *channel*. Give the flag a
        recording whose channel carries nothing but timekeeping, or a window its events fall
        outside, and the run writes an annotations.csv holding one header line, prints "Wrote
        … annotations.csv 0 rows", exits 0 and passes --strict — which is exactly the shape
        EMPTY_WINDOW exists to refuse on the signal side, arriving by the other route.

        Only when there is no signal table either, so this is the whole of what the run
        produced. A windowed conversion of a recording whose events sit elsewhere is
        ordinary and its signals are the point; warning there would fire on most windows of
        most annotated recordings, which is how a warning stops being read.
      */
      if (!plan.writeSignals && annotationsWritten === 0) {
        plan.diagnostics.push(emptyAnnotations(annotationData.annotations.length, window, plan.gzip));
      }
    }

    written.push(
      await writeChannelsCsv(outputDir, file, plan, options.gzip === true, options.bom === true),
    );

    /*
      The file moved under the conversion. Said out loud, because nothing else shows it.

      The CSVs are still correct for the records that were read, and metadata.json still
      describes the file as it was opened — so the record is consistent with the output
      whatever happens here. What stops being true is that the checksum describes the bytes
      that were converted, since an in-place overwrite leaves nowhere to read them from. It
      is dropped rather than guessed at.
    */
    const changed = await file.changedSinceOpen();
    if (changed) plan.diagnostics.push(inputChanged(options.checksum === true));

    await writeMetadata(
      outputDir,
      inputPath,
      file,
      plan,
      written,
      annotationsWritten,
      changed ? null : checksumAtOpen,
      timing.starts !== null,
      options.bom === true,
    );

    const stale = await findStaleOutput(outputDir, written);

    return {
      outputDir,
      files: written,
      readerHungUp: false,
      annotationCount: annotationsWritten,
      // The directory path needs it too: `--gzip` changes the names, and two of these
      // sentences are a file name and nothing else. See withSidecarsNamed.
      diagnostics: withSidecarsNamed(
        withSignalTableUnwritten(
          [
            ...withTimingPromiseKept(withoutFileRateWarning(file.diagnostics), timing.starts !== null),
            ...plan.diagnostics,
            ...stale,
          ],
          plan.writeSignals,
          plan.gzip,
          convertedChannels(plan),
          false,
          plan.layout === 'long',
          channelsWithoutRows(plan),
        ),
        { toStdout: false, gzip: plan.gzip },
      ),
      plan,
      file,
      elapsedMs: Date.now() - startedAt,
    };
  } finally {
    await file.close();
  }
}

/*
  Files this tool produces, used to spot leftovers from an earlier conversion.

  The rate part has to allow every shape a filename can now take, not just the plain ones:

    signals_256hz.csv         an integer rate
    signals_12_5hz.csv        a fractional rate, decimal point written as an underscore
    signals_1_000e-7hz.csv    a rate small enough to need exponent form  (0.2.2)
    signals_256hz.csv.gz      any of the above, compressed                (0.3.0)
    signals_0hz_2.csv         a second group whose rate slug collided    (0.2.1)

  The previous `[\w.]+hz` matched neither of the last two — `-` and `+` are not word
  characters, and the collision suffix falls after the `hz`. Both were introduced by
  recent changes and both silently stopped being recognised as this tool's own output, so
  leftovers of exactly those kinds went unreported: the one situation the warning exists
  for. Requiring a digit after the underscore keeps a user's own `signals_notes.csv` out.

  And `signals_Infinityhz.csv`, which is a name this tool writes and the digit rule then
  excluded. A record duration too small to divide into overflows the rate — `rateSlug` says
  `Infinityhz` and the writer opens that file, with the TIME_RESOLUTION warning beside it
  saying no rows will go in it — so a mixed-rate run of such a file leaves two `signals_*`
  files behind and the next conversion into that directory named one of them:

      warning: signals_1e+308hz.csv is left over from an earlier conversion into this
               directory and was not rewritten.

  with `signals_Infinityhz.csv` sitting beside it, from the same run, unmentioned. Spelled
  out rather than loosened, so a user's own `signals_notes.csv` stays out for the reason
  above; the check below holds the pattern against every name `rateSlug` can produce.
*/
const OUTPUT_PATTERN =
  /^(signals(_(\d[\w.+-]*|Infinity)hz(_\d+)?)?\.csv(\.gz)?|annotations\.csv(\.gz)?|channels\.csv(\.gz)?|metadata\.json)$/u;

/**
 * Detect output from a previous run that this one did not replace.
 *
 * `--force` overwrites files but does not empty the directory, so converting a
 * mixed-rate recording and then a single-rate one into the same place leaves
 * `signals_256hz.csv` sitting next to a fresh `signals.csv`. Both look current.
 * Nothing is deleted here — the user is told, and decides.
 */
async function findStaleOutput(
  outputDir: string,
  written: readonly WrittenFile[],
): Promise<Diagnostic[]> {
  const entries = await readdir(outputDir).catch(() => null);
  if (!entries) return [];

  // metadata.json is rewritten on every run but is not part of the reported file list.
  const fresh = new Set([...written.map((f) => f.name), 'metadata.json']);
  const stale = entries.filter((name) => OUTPUT_PATTERN.test(name) && !fresh.has(name)).sort();
  if (stale.length === 0) return [];

  return [
    {
      code: 'STALE_OUTPUT',
      severity: 'warning',
      /*
        Through `listed`, like every other message that enumerates something this run does not
        control. How many stale files a directory holds is up to the directory, and a
        mixed-rate recording converted into a reused one is exactly how it fills up: 120 old
        `signals_<rate>hz.csv` files produced a single 2,373-character warning line. That is
        the failure `listed` was written for — its own comment quotes the 1,600-character
        version of it — and this was the one message still joining its own list.

        The hint said "Delete them" whatever the count, so one stale file read "signals_999hz
        .csv is left over ... Delete them."
      */
      message:
        `${listed(stale)} ${stale.length === 1 ? 'is' : 'are'} left over from an earlier ` +
        `conversion into this directory and ${stale.length === 1 ? 'was' : 'were'} not rewritten.`,
      hint:
        `Delete ${stale.length === 1 ? 'it' : 'them'}, or convert into a fresh directory, so ` +
        'the two runs do not get mixed up.',
    },
  ];
}

export function defaultOutputDir(inputPath: string): string {
  /*
    The third export whose only argument is a path, and the one left handing a wrong value on.

    `path.basename` refuses a non-string with Node's own text — `The "path" argument must be
    of type string. Received type number (5)` — which names a parameter of `path`, not one of
    this function, and reaches a caller who never called `path`. `convert` got this check in
    0.7.x and `EdfFile.open` in 0.8.9; this is what the api page tells you to call to find out
    where those two would write, so it is reached by exactly the same caller.
  */
  assertInputPath(inputPath);
  const base = path.basename(inputPath, path.extname(inputPath));
  return path.join(path.dirname(inputPath), `${base}_csv`);
}

/** Never let an output target resolve to the recording being read, even with --force. */
async function assertInputDoesNotOverlapOutputs(
  inputPath: string,
  outputDir: string,
  file: EdfFile,
  plan: ConversionPlan,
  options: ConvertOptions,
): Promise<void> {
  /*
    The names this run will actually write, compressed ones included.

    The rate files come from the plan and already carry `.csv.gz` under --gzip; the sidecars
    were spelled out here and did not. So a compressed run checked two names it would never
    write and missed the two it would: a recording sitting at <outdir>/channels.csv.gz was
    overwritten by its own conversion, with --force, reported as a success. The same file
    named signals.csv.gz was refused, which is what gives the oversight away.
  */
  const suffix = options.gzip === true ? '.csv.gz' : '.csv';
  const names = new Set(plan.groups.map((group) => group.fileName));
  if (file.annotationSignals.length > 0) names.add(`annotations${suffix}`);
  names.add(`channels${suffix}`);
  names.add('metadata.json');

  const inputResolved = path.resolve(inputPath);
  /*
    Tolerant of the input going away, like every other stat in this function.

    This one was bare, so a recording renamed or unlinked between `EdfFile.open` and here threw
    Node's own error straight out of `convert()`:

        Error: ENOENT: no such file or directory, stat './link.edf'

    with no `code` this tool defines and no hint — a sixth error type from a function the API
    page says throws five, on the one path where a file moving under a conversion is the
    documented ordinary case. The descriptor is already open, so the conversion itself is fine;
    what is lost is the inode comparison, and `samePath` below still answers the case that
    matters when the two names are the same name.
  */
  const inputInfo = await stat(inputPath).catch(() => null);

  for (const name of names) {
    const target = path.join(outputDir, name);
    const targetResolved = path.resolve(target);
    const targetInfo = await stat(target).catch(() => null);
    const samePath = targetResolved === inputResolved;
    const sameFile =
      targetInfo !== null &&
      inputInfo !== null &&
      targetInfo.dev === inputInfo.dev &&
      targetInfo.ino === inputInfo.ino;
    if (!samePath && !sameFile) continue;

    throw new ConversionError(
      'INPUT_OUTPUT_COLLISION',
      `Output file "${target}" is the same file as the input recording.`,
      'Choose a separate directory with --out. The input was not modified.',
    );
  }
}

async function prepareOutputDir(dir: string, force: boolean): Promise<void> {
  /*
    The destination, quoted into every refusal below, and shown rather than pasted.

    `--out` is whatever the caller's shell handed over, and `printableLines` escapes every
    control byte in a message except the one that makes a line break — it splits on those
    first, to keep the author's own lines. So a newline in a destination cut the refusal's
    first line in half:

        error: "out/ex
               ists" already exists.

    which is the line this tool's comments keep insisting stays whole, because it is the one a
    log gets grepped for. Same in `EdfFile.open`'s path and in a `--channels` term; all three
    go through `printable` now, where the summary and the report have always put them.
  */
  const shownDir = printable(dir);
  /*
    Claim the directory with a single atomic mkdir rather than asking whether it exists
    and then creating it.

    Checking first left a window between the two: two conversions started together both
    saw "not there", both proceeded, and both opened write streams on the same signals.csv.
    Neither reported anything — both exited 0, having half-written one file between them.
    A non-recursive mkdir cannot do that. Exactly one caller creates the directory; every
    other one gets EEXIST from the filesystem and takes the already-exists path below.

    Parents are still created recursively, since --out ./a/b/c should work. Only the final
    component is the claim.
  */
  const parent = path.dirname(dir);
  if (parent && parent !== dir) {
    try {
      await mkdir(parent, { recursive: true });
    } catch (cause: unknown) {
      // A parent that is a regular file surfaces as EEXIST naming the parent, which reads
      // as though the destination already exists rather than as "you cannot put a
      // directory inside a file". Name the real obstacle instead of the errno.
      const info = await stat(parent).catch(() => null);
      if (info && !info.isDirectory()) {
        throw new ConversionError(
          'OUTPUT_UNWRITABLE',
          `Cannot create "${shownDir}": "${printable(parent)}" is a file, not a directory.`,
          'Choose a destination whose parent directories are directories, with --out.',
        );
      }
      throw new ConversionError(
        'OUTPUT_UNWRITABLE',
        `Cannot create "${shownDir}": ${describeFsError(cause)}.`,
        createHint(cause),
      );
    }
  }

  let claimed = true;
  try {
    await mkdir(dir);
  } catch (cause: unknown) {
    if ((cause as NodeJS.ErrnoException).code !== 'EEXIST') {
      throw new ConversionError(
        'OUTPUT_UNWRITABLE',
        `Cannot create "${shownDir}": ${describeFsError(cause)}.`,
        createHint(cause),
      );
    }
    claimed = false;
  }

  if (!claimed) {
    const existing = await stat(dir).catch(() => null);

    // --force means "replace my previous output", not "write output files into
    // whatever this happens to be". Pointing it at a regular file needs saying so.
    if (existing && !existing.isDirectory()) {
      throw new ConversionError(
        'OUTPUT_UNWRITABLE',
        `"${shownDir}" is a file, but the converted data needs a directory.`,
        'Choose a directory with --out.',
      );
    }
    /*
      A link to nothing, which `stat` cannot see and `mkdir` will not write through.

      `stat` follows symbolic links, so a dangling one leaves `existing` null and fell through
      to the sentence below — which is the one claim that is not true of it:

          $ edf2csv rec.edf --out link-to-nowhere
          error: "link-to-nowhere" already exists.
                 Pass --force to overwrite it, or --out to choose a different directory.

      Nothing is there. And following that advice made it worse: `--force` reached the writer
      and came back as `Writing to "link-to-nowhere" failed: part of the path does not exist.
      The files written so far are incomplete and should not be used` — a conversion failure,
      about files that were never written, advising the reader to make sure nothing is
      removing a directory that never existed.

      A broken link in a batch's destination is how this arrives: the run before it wrote into
      a mount that has since gone.
    */
    if (!existing && (await lstat(dir).catch(() => null))) {
      throw new ConversionError(
        'OUTPUT_UNWRITABLE',
        `"${shownDir}" is a symbolic link to something that does not exist, so nothing can be ` +
          `written there.`,
        'Remove the link, or choose a directory with --out. --force writes into a directory ' +
          'that is already there, and a link to nowhere is not one.',
      );
    }
    if (!force) {
      /*
        What `--force` does, rather than what it sounds like it does.

        "Overwrite it" reads as a claim about the directory, and the neighbouring hint said so
        outright — "--force replaces a previous output directory". It does neither. It writes
        into the directory: files of the same name are replaced and everything else is left
        exactly where it was, which is why `STALE_OUTPUT` exists to report what stayed. The
        flag list has described it accurately since it was written — "write into the output
        directory if it already exists" — so the one sentence a reader meets at the moment
        they decide whether to pass it was the one contradicting the other two.

        The difference is a directory that ends up holding two conversions. Convert a
        mixed-rate recording and then a single-rate one into the same place with `--force`,
        and `signals_256hz.csv` sits beside a fresh `signals.csv` under a channels.csv and a
        metadata.json describing only the second — which is exactly the outcome a reader who
        was told the directory would be replaced does not expect, and does not check for.
      */
      throw new ConversionError(
        'OUTPUT_EXISTS',
        `"${shownDir}" already exists.`,
        'Pass --force to write into it, leaving whatever else it holds, or --out to choose ' +
          'a different directory.',
      );
    }
  }
}

/**
 * Turn a Node filesystem error into something a person can act on: an errno as a sentence,
 * so Node's own text never reaches the screen.
 *
 * Exported for the CLI's last-resort stdout listener, which is the third and last place a
 * write failure becomes a message and the one that was still printing `ENOSPC: no space left
 * on device, write`.
 */
export function describeFsError(cause: unknown): string {
  const code = (cause as NodeJS.ErrnoException | undefined)?.code;
  if (code === 'EACCES' || code === 'EPERM') return 'permission denied';
  if (code === 'ENOSPC') return 'the disk is full';
  // The one that actually happens on a shared filesystem, and the one this list left out.
  if (code === 'EDQUOT') return 'you are over your disk quota on this filesystem';
  if (code === 'ENOTDIR') return 'part of the path is a file, not a directory';
  if (code === 'EROFS') return 'the filesystem is read-only';
  if (code === 'ENAMETOOLONG') return 'the path is too long';
  /*
    The two `writeHint` knew and this did not, which showed once the write failures started
    coming through here: `writeHint` reads the same errno off the same error and has a
    sentence for eleven of them, this had seven, and the gap was the two that only a write
    reaches. `EISDIR` is a directory sitting where an output file belongs, which is the
    commonest way to make one of these happen on purpose.

    `EPIPE` is deliberately not here. The writer treats a reader hanging up as the ordinary
    end of a pipeline rather than as a failure, so it never becomes a message.
  */
  if (code === 'EISDIR') return 'a directory is sitting there already';
  if (code === 'EMFILE' || code === 'ENFILE') return 'too many files are open';
  /*
    The one this list could not describe, and the recursive mkdir above is what makes it
    surprising: every parent is created on the way, so "no such file or directory" is not a
    parent that was missing. It is a component that exists and leads nowhere — a symbolic
    link with no target is the way to get one — or a directory removed by something else
    between the two calls.

    Missing it meant the fallback ran, which is Node's own text, complete with the internal
    call that raised it:

        error: Cannot create "dangle/x": ENOENT: no such file or directory, mkdir 'dangle'.
               Check the path exists and that you have permission to write there.

    Keeping that off the screen is the whole purpose of the six lines above it.
  */
  if (code === 'ENOENT') return 'part of the path does not exist';
  return cause instanceof Error ? cause.message : String(cause);
}

async function writeSignalFiles(
  file: EdfFile,
  plan: ConversionPlan,
  outputDir: string | null,
  recordStarts: Float64Array | null,
  options: ConvertOptions,
  onHangUp?: (hungUp: boolean) => void,
): Promise<WrittenFile[]> {
  // One budget for every table in this conversion, not one per table: see OffsetBudget.
  const offsets = newOffsetBudget();
  // Likewise one for every channel, not one per channel: see SampleCacheBudget.
  const sampleCaches = newSampleCacheBudget();
  // Only the stdout path needs this; --out finds a full disk on its own, because it always
  // has another file to write afterwards. See auditStdout.
  const audit = outputDir === null ? auditStdout() : null;
  /*
    In the long layout every group writes into one table, so they share one stream. Opening
    a stream per group on the same path is what the rate-slug collision fix in groupByRate
    was about: two writers on one file interleave rows under a header naming one of them.
  */
  let shared: {
    stream: Writable;
    settled: Promise<void>;
    release: () => void;
    target: Writable;
    writer: BufferedLineWriter;
  } | null = null;

  /*
    One buffer's worth of memory for the conversion, not one per table.

    Every group had its own writer at the default 1 MiB threshold, so pending output was
    group count × 1 MiB before anything drained. A 6.5 MB recording with 40 sampling rates
    — the header allows thousands of channels, and a research montage really does mix a
    dozen rates — died with a raw V8 heap out-of-memory and exit 134 under a 96 MB cap,
    while the site advertises 48 MB. The recording is small; it is the fan-out that is not.

    Split evenly with a floor, so the single-rate case, which is nearly every recording,
    keeps exactly the buffer it always had, and forty tables cost one megabyte between them
    rather than forty. The floor is what the sum is bounded by rather than the split: past
    128 groups every table takes the 8 KiB minimum, so the total grows again from there.
    The long layout shares one writer already and is unaffected either way.
  */
  const MIN_FLUSH_THRESHOLD = 8 * 1024;
  const flushThreshold = Math.max(
    MIN_FLUSH_THRESHOLD,
    Math.floor(DEFAULT_FLUSH_THRESHOLD / Math.max(1, plan.groups.length)),
  );
  /*
    The stream's own buffer is the other half of the same sum.

    0.5.6 shared the line-buffer budget and left `createWriteStream` at its 64 KiB default,
    which is per stream: 200 rate groups meant 12.8 MB of stream buffer on top of 12.8 MB of
    line buffer, and an 855 KB recording still died at a 48 MB cap. Shared the same way, with
    a floor that keeps an ordinary conversion writing in useful-sized pieces.
  */
  const streamBuffer = Math.max(
    16 * 1024,
    Math.floor(DEFAULT_FLUSH_THRESHOLD / Math.max(1, plan.groups.length)),
  );

  const open: OpenGroup[] = plan.groups.map((group, groupIndex) => {
    // A null directory means the single table goes to stdout. process.stdout is already a
    // writable stream, so the same buffered writer and backpressure handling apply.
    const target =
      shared?.target ??
      (outputDir === null
        ? process.stdout
        : createWriteStream(path.join(outputDir, group.fileName), {
            highWaterMark: streamBuffer,
          }));
    const { stream, settled, release } = shared ?? compressed(target, options.gzip === true);
    /*
      Under --gzip the writer feeds the compressor, so its byte count is the CSV before
      compression and says nothing about what reached the descriptor. The compressor's own
      output is what stdout is handed, so that is what is counted. `pipe` uses a 'data'
      listener of its own and a second one is delivered the same chunks.

      Once per stream, which in the long layout is once for all the groups. Attaching per
      group put N listeners on the one shared compressor, so every chunk was counted N
      times: `--stdout --layout long --gzip` on a 40-rate recording claimed 622,240 of
      622,240 bytes where 15,556 had been written, failed with a disk-full error over a
      perfectly good file, and printed Node's MaxListenersExceededWarning to stderr on the
      way past ten. 0.5.4 fixed the same arithmetic in the uncompressed branch and left
      this one, because the uncompressed branch is where the count is a sum and this one is
      where it is a subscription.
    */
    if (audit && stream !== target && !shared) {
      stream.on('data', (chunk: Buffer) => audit.count(chunk.length));
    }
    /*
      One writer, not one per group, when the table is shared. Separate writers over one
      stream each hold their own buffer and flush on their own schedule, so the rows would
      reach the file in whatever order the buffers filled — which is not the order they
      were produced in, and the long layout's whole claim is that its rows are in time
      order.
    */
    const writer = shared?.writer ?? new BufferedLineWriter(stream, flushThreshold);
    if (plan.layout === 'long' && !shared) shared = { stream, settled, release, target, writer };

    // Only the first group writes the header of a shared table, and the mark before it.
    if (plan.layout !== 'long' || groupIndex === 0) {
      if (options.bom === true) writer.push(UTF8_BOM);
      writer.pushLine(
        plan.layout === 'long'
          ? csvRow([TIME_COLUMN, 'channel', 'value'])
          : csvRow([TIME_COLUMN, ...group.channels.map((c) => c.column)]),
      );
    }
    return {
      group,
      writer,
      formatters: group.channels.map((c) => makeSampleFormatter(c.signal, c.decimals, sampleCaches)),
      formatTime: makeTimeFormatter(
        group.samplesPerRecord,
        group.rate,
        group.timeDecimals,
        offsets,
      ),
      rows: 0,
      settled,
      release,
    };
  });

  try {
    const written = await streamSignalRows(file, plan, open, recordStarts, options);

    /*
      Checked once everything has been flushed and ended, and not when the reader hung up.

      `--stdout | head -1` closes the pipe on purpose, which is a shell idiom rather than a
      failure. A pipe is not a regular file, so auditStdout declines it anyway — the second
      guard is here because the cost of getting this one wrong is reporting a failure for a
      command that worked.
    */
    if (audit) {
      // Uncompressed, the writer hands its bytes straight to the descriptor; compressed,
      // they were counted on the compressor's way out.
      if (options.gzip !== true) {
        /*
          Once per writer, not once per group. The long layout gives every group the same
          writer, so counting per group multiplied its byte total by the number of rates:
          `--stdout --layout long` on a three-rate recording handed over 32,043 bytes, was
          credited with 96,129, and failed with a disk-full error for a file that was
          complete on disk. It only showed with stdout redirected to a regular file, since
          that is the one case the audit applies to — which is the command the --layout
          documentation gives.
        */
        for (const writer of new Set(open.map((entry) => entry.writer))) {
          audit.count(writer.bytesOut);
        }
      }
      if (!open.some((entry) => entry.writer.hungUp)) audit.verify();
    }
    onHangUp?.(open.some((entry) => entry.writer.hungUp));
    return written;
  } catch (cause) {
    for (const entry of open) entry.writer.destroy();

    /*
      Reading and writing both fail through here, and both were reported as writing.

      A recording that shrinks mid-conversion — still being written by the acquisition
      software, say — raises the reader's own error, which names the record and says the
      file changed size while it was being read. That precise diagnosis was then filed under
      `Writing to "<dir>" failed` and given the hint about freeing disk space, which sends
      someone to look at the one part of the system that was working.

      The reader's message and its advice are kept; only the note about partial output is
      added, since that much is true of either failure.
    */
    // A ConversionError arrived already saying what went wrong — a callback that threw, say.
    // Wrapping it again turned "the onProgress callback threw" into `Writing to "out"
    // failed: the onProgress callback threw`, under a hint about checking the destination.
    if (cause instanceof ConversionError) throw cause;

    if (cause instanceof EdfError) {
      const where = outputDir === null ? 'stdout' : `"${outputDir}"`;
      throw new ConversionError(
        'INPUT_UNREADABLE',
        cause.message,
        `${cause.hint ? `${cause.hint} ` : ''}What was written to ${where} before it failed is ` +
          `incomplete and should not be used.`,
      );
    }

    /*
      Through `describeFsError`, like the destination errors above, and not through Node's
      own text.

      That function exists to keep an errno and the internal call that raised it off the
      screen — its comment says so in as many words, quoting the leak it was written for:
      `Cannot create "dangle/x": ENOENT: no such file or directory, mkdir 'dangle'.` The
      write failures never joined it, so a directory sitting where signals.csv belongs read

          error: Writing to "out" failed: EISDIR: illegal operation on a directory, open 'out/signals.csv'

      with the errno token, the syscall, the path repeated, and no full stop — the one
      diagnostic shape this tool went back and fixed. The hint printed directly under it has
      always read the same errno and said "A directory is sitting where that file belongs",
      so the answer was in hand; it was the sentence above it that had not asked.
    */
    throw new ConversionError(
      'WRITE_FAILED',
      `Writing to ${outputDir === null ? 'stdout' : `"${outputDir}"`} failed: ` +
        `${describeFsError(cause)}.`,
      writeHint(cause, outputDir === null),
    );
  }
}

/**
 * Whether two sample times are close enough that only rounding could separate them.
 *
 * A relative epsilon, because the gap between doubles grows with magnitude. Well below any
 * real sample interval — the finest a recording can declare is bounded by its record
 * duration and samples-per-record fields — and well above the one-ULP disagreement that two
 * exact divisions of the same instant produce.
 */
function nearlyEqual(a: number, b: number): boolean {
  return Math.abs(a - b) <= Math.max(Math.abs(a), Math.abs(b)) * 1e-12;
}

/**
 * One record's samples in the long layout: `time_s,channel,value`, in time order.
 *
 * The groups are merged rather than written one after another. Every sample in a record
 * falls inside that record's span, so taking the earliest next sample across the groups
 * each time leaves the whole file sorted by `time_s` — which is the only thing that makes a
 * mixed-rate long table useful, since sorting 3 million rows afterwards is the reader's
 * problem and a large one.
 *
 * Ties go to the group with the higher rate, which is the order the groups are already in.
 * Within a sample time the channels come out in the order the file declares them.
 */
async function writeLongRecord(
  file: EdfFile,
  open: readonly OpenGroup[],
  batch: RecordBatch,
  recordInBatch: number,
  recordStart: number,
  range: ConversionPlan['range'],
): Promise<void> {
  const writer = open[0]?.writer;
  if (!writer) return;

  const cursors = new Int32Array(open.length);
  /* Reused across sample times so a three-million-row conversion allocates one of these. */
  const due: { entry: OpenGroup; groupIndex: number; sample: number; channel: number }[] = [];

  for (;;) {
    let earliest = Infinity;
    let source = -1;
    let sourceSample = 0;
    for (let g = 0; g < open.length; g++) {
      const entry = open[g];
      if (!entry) continue;
      const sample = cursors[g] ?? 0;
      if (sample >= entry.group.samplesPerRecord) continue;
      const time = recordStart + sample / entry.group.rate;
      if (time < earliest) {
        earliest = time;
        source = g;
        sourceSample = sample;
      }
    }
    if (earliest === Infinity) return;

    /*
      Everything at this instant, in the order the file declares its channels.

      Groups are ordered by rate, largest first, because that is how the wide layout names
      its files. Emitting a tie group by group therefore ordered the channels by descending
      sampling rate — so a recording declaring `slow, medium, fast` wrote `fast, medium,
      slow`, while the documentation promised file order and channels.csv listed file order.
      Signal index is the file's own order, and the only one a reader can predict.

      "At this instant" is decided on the time as written, not on the double. `s / rate` for
      two channels at one moment need not give the same double: a 0.3 s record holding 12 and
      4 samples makes 40 Hz and 13.333… Hz, and 9/40 is 0.22500000000000000555 while 3/13.333…
      is 0.22499999999999997780. Equality missed that, so those two rows fell out in numeric
      order — `slow` before `fast`, once, in the middle of a file that was otherwise right.

      Two rows are at one time exactly when they carry the same `time_s`, which is the only
      definition a reader of the CSV can apply. The numeric pre-filter keeps the common case
      to one comparison; formatting happens only for candidates already within a hair.
    */
    let earliestText: string | null = null;
    /*
      Formatted on the first near-tie and not before, which on a single-rate table is never.

      The group and sample that set `earliest` are captured above rather than searched for
      again, since the loop below advances cursors as it goes and a later search would be
      looking at a table that had already moved on.
    */
    const asWritten = (): string =>
      (earliestText ??= open[source]?.formatTime(recordStart, sourceSample) ?? '');
    due.length = 0;
    for (let g = 0; g < open.length; g++) {
      const entry = open[g];
      if (!entry) continue;
      const sample = cursors[g] ?? 0;
      if (sample >= entry.group.samplesPerRecord) continue;
      const time = recordStart + sample / entry.group.rate;
      if (time !== earliest) {
        // Far away in the ordinary case; only a near-tie is worth formatting.
        if (!nearlyEqual(time, earliest)) continue;
        if (entry.formatTime(recordStart, sample) !== asWritten()) continue;
      }
      cursors[g] = sample + 1;
      // Same window rule as the wide layout, with the same per-rate slack.
      if (
        !sampleTimeIsInRange(earliest, range.startSeconds, range.endSeconds, toleranceFor(entry.group.rate))
      ) {
        continue;
      }
      for (let c = 0; c < entry.group.channels.length; c++) {
        due.push({ entry, groupIndex: g, sample, channel: c });
      }
    }
    if (due.length === 0) continue;
    if (due.length > 1) {
      due.sort(
        (a, b) =>
          (a.entry.group.channels[a.channel]?.signal.index ?? 0) -
          (b.entry.group.channels[b.channel]?.signal.index ?? 0),
      );
    }

    for (const item of due) {
      const channel = item.entry.group.channels[item.channel];
      const format = item.entry.formatters[item.channel];
      if (!channel || !format) continue;
      if (writer.hungUp) return;
      writer.pushLine(
        `${item.entry.formatTime(recordStart, item.sample)},${escapeCsvField(channel.column)},` +
          `${format(file.sampleAt(batch, recordInBatch, channel.signal, item.sample))}`,
      );
      item.entry.rows++;
      // Flushed inside the record for the same reason the wide layout is; see there.
      if (writer.full) await writer.flush();
    }
  }
}

async function streamSignalRows(
  file: EdfFile,
  plan: ConversionPlan,
  open: OpenGroup[],
  recordStarts: Float64Array | null,
  options: ConvertOptions,
): Promise<WrittenFile[]> {
  const { startSeconds, endSeconds, startRecord, endRecord } = plan.range;
  const { recordDuration } = file.header;
  let recordsDone = 0;

  /*
    Every destination has hung up, so nothing formatted from here on could reach anyone.
    `--stdout | head -1` is the usual way to arrive here: the reader takes one line and
    closes the pipe while the conversion is still near the start of the recording.

    Stopping also makes the reported row count mean what it says — rows that reached the
    consumer, rather than rows the loop went on formatting into a discarded buffer.
  */
  const allHungUp = (): boolean => open.length > 0 && open.every((entry) => entry.writer.hungUp);

  for await (const batch of file.readRecords({ startRecord, endRecord })) {
    if (allHungUp()) break;
    for (let r = 0; r < batch.recordCount; r++) {
      if (allHungUp()) break;
      const index = batch.firstRecordIndex + r;
      const recordStart = recordStarts ? (recordStarts[index] ?? index * recordDuration) : index * recordDuration;

      if (plan.layout === 'long') {
        await writeLongRecord(file, open, batch, r, recordStart, plan.range);
        recordsDone++;
        continue;
      }

      for (const entry of open) {
        const { group, writer, formatters, formatTime } = entry;
        const { channels, rate } = group;
        // Slack that never reaches the next sample; see toleranceFor.
        const slack = toleranceFor(rate);

        for (let sample = 0; sample < group.samplesPerRecord; sample++) {
          const time = recordStart + sample / rate;
          if (!sampleTimeIsInRange(time, startSeconds, endSeconds, slack)) continue;

          let row = formatTime(recordStart, sample);
          for (let c = 0; c < channels.length; c++) {
            const channel = channels[c];
            const format = formatters[c];
            if (!channel || !format) continue;
            row += ',' + format(file.sampleAt(batch, r, channel.signal, sample));
          }
          if (writer.hungUp) break;
          writer.pushLine(row);
          entry.rows++;

          /*
            Flushed inside the record, not only at the end of one.

            The buffer was drained once per record, so the rows of a single record piled up
            with nothing emptying them — memory followed samples-per-record rather than the
            batch size the writer exists to hold to. One record of 16,000,000 samples died
            with a heap out of memory under a 256 MB cap, while the same 32 MB of samples
            split into 16,000 records converted to the same 283 MB CSV without trouble. The
            format allows either layout and says nothing about which to expect.

            `full` is a synchronous read of the pending size, so the twenty million rows that
            are not at a boundary cost a comparison rather than a microtask each.
          */
          if (writer.full) await writer.flush();
        }
        await writer.maybeFlush();
      }

      recordsDone++;
    }

    if (options.onProgress) {
      /*
        A caller's callback is the caller's, and its failures are not the destination's.

        This ran inside the same try that turns a stream failure into WRITE_FAILED, so a
        progress callback that threw came back as `Writing to "out" failed: caller bug`,
        advising the reader to check a destination that was working perfectly. It is the
        same misattribution the write hints had until 0.4.36, one layer up.

        The original is kept as `cause`, so the stack that actually matters survives, and
        the conversion still stops — the callback threw, and carrying on writing into a
        directory whose owner has just failed is not an improvement.
      */
      try {
        options.onProgress({
          recordsDone,
          recordsTotal: endRecord - startRecord,
          // Once per writer: the long layout's groups share one, so summing per group
          // reported a figure larger than the file being written.
          bytesWritten: [...new Set(open.map((entry) => entry.writer))].reduce(
            (sum, entry) => sum + entry.charsWritten,
            0,
          ),
        });
      } catch (cause) {
        throw new ConversionError(
          'CALLBACK_FAILED',
          `The onProgress callback threw: ${cause instanceof Error ? cause.message : String(cause)}`,
          'This is the caller\'s callback, not the recording or the destination. Whatever was ' +
            'written before it threw is incomplete and should not be used.',
          { cause },
        );
      }
    }
  }

  const closed = new Set<BufferedLineWriter>();
  for (const entry of open) {
    if (closed.has(entry.writer)) continue;
    closed.add(entry.writer);
    try {
      await entry.writer.end();
      // With --gzip the writer's stream is the compressor, whose end callback fires when the
      // compressor is done rather than when the file behind it is. Awaiting only that would
      // report success with the tail of the file still in flight.
      await entry.settled;
    } finally {
      // In a finally for the reason 0.5.45 gives about the writer's own release: a
      // conversion that fails here still has to leave process.stdout as it found it, and a
      // failure is exactly when a caller goes on to convert something else.
      entry.release();
    }
  }

  // A shared table is one file, and its row count is every group's rows, not the first's.
  if (plan.layout === 'long') {
    const first = open[0];
    if (!first) return [];
    return [{ name: first.group.fileName, rows: open.reduce((sum, e) => sum + e.rows, 0) }];
  }
  return open.map((entry) => ({ name: entry.group.fileName, rows: entry.rows }));
}

/**
 * The stream rows are written to, plus a promise for the data reaching its destination.
 *
 * Compression sits between the writer and the file as a transform. Failures below it — a
 * full disk, an unwritable path — surface on the file stream, where nothing is listening,
 * so they are forwarded onto the compressor: that is the stream the writer watches, and
 * routing them there keeps one error path rather than two.
 */
function compressed(
  target: Writable,
  gzip: boolean,
): { stream: Writable; settled: Promise<void>; release: () => void } {
  if (!gzip) return { stream: target, settled: Promise.resolve(), release: (): void => {} };
  const compressor = createGzip();
  /*
    Removable, because one of the streams this can be handed outlives the conversion.

    0.5.36 fixed exactly this leak for the writer's own listener: a library caller running
    twelve `toStdout` conversions left twelve 'error' listeners on `process.stdout` and got
    Node's MaxListenersExceededWarning on the eleventh. That fix is `BufferedLineWriter`'s
    `#release()`, and it cannot reach this one — under `gzip` the writer's stream is the
    compressor, and `process.stdout` is only ever the compressor's destination. So the same
    leak survived on the same stream, behind one extra flag, and the regression test written
    to catch it does not pass `gzip: true`.

    A file stream is a different matter and needs no release: it is created for this
    conversion and closed with it. The discipline is the writer's — release only from a
    stream this tool does not own.
  */
  const forward = (error: Error): void => {
    compressor.destroy(error);
  };
  target.on('error', forward);
  const release = (): void => {
    if (target === process.stdout || target === process.stderr) target.off('error', forward);
  };

  /*
    pipe() ends its destination when the source ends, and stdout must survive the
    conversion: the writer already refuses to close it, because a closed stdout breaks
    every later write in the process. Nothing waits on stdout either — it is not this
    tool's to finish.
  */
  const toStdout = target === process.stdout;
  compressor.pipe(target, { end: !toStdout });
  if (toStdout) {
    /*
      Waited on, even though stdout is not ours to end.

      This returned an already-resolved promise, so `await entry.settled` waited for nothing
      and the conversion declared itself finished while the compressor still held the tail of
      the stream. `--stdout --gzip` onto a destination that filled up therefore printed the
      ENOSPC *and then* "Wrote 102,400 rows to stdout." — and exited 0, over a file 11,270
      bytes short whose gzip member has no trailer and will not decompress. Through `--out`,
      on the same volume with the same space, the identical failure is reported and exits 1.

      The byte audit could not see it either: it stats the descriptor as soon as the writers
      are done, which on this path is before the compressor has pushed its last chunks.

      `finished(compressor)` is the source side, not the destination — it resolves when the
      compressor has flushed everything into stdout, and it is stdout's own write that fails.
      That keeps `end: !toStdout` exactly as it was: nothing here closes stdout.
    */
    const flushed = finished(compressor).catch((error: unknown) => {
      /*
        EPIPE is not a failure here, and turning it into one is the trap this nearly fell
        into: `--stdout --gzip | head` is an ordinary thing to type, and the documented
        answer to it is "Stopped: the reader closed the pipe after 52,507 of 102,400 rows
        had been written", exit 0. Waiting on the compressor surfaced the EPIPE that the
        uncompressed path already routes through the writer's hang-up flag — the writer
        sees the same error, forwarded, and records it — so the wait has to let that one
        through and keep everything else.
      */
      if ((error as NodeJS.ErrnoException | null)?.code === 'EPIPE') return;
      throw error;
    });
    flushed.catch(() => {});
    return { stream: compressor, settled: flushed, release };
  }

  /*
    A failure under the compressor rejects both this promise and the writer's own end(),
    and end() is the one awaited first. Without a handler attached here that rejection
    belonged to nobody, and Node killed the process over it: `--gzip` into an unwritable
    path printed a raw EISDIR stack trace instead of the ordinary "Writing to ... failed"
    message and exit 1, which is what the same path does uncompressed.

    Attaching the handler is enough to make it handled. Awaiting `settled` downstream still
    reports the failure in the case where end() happened to succeed.
  */
  const settled = finished(target);
  settled.catch(() => {});
  return { stream: compressor, settled, release };
}

/**
 * Write one of the sidecar files, reporting a failure the way the signal writer does.
 *
 * These three used to call `writeFile` bare, so a failure escaped as whatever the
 * filesystem said — `EISDIR: illegal operation on a directory, open '...'` with no hint
 * and, more importantly, no mention that the signal files had already been written. The
 * conversion stopped half-done and the message gave no sign of it.
 */
async function writeOutputFile(
  outputDir: string,
  name: string,
  contents: string,
  gzip = false,
  bom = false,
): Promise<void> {
  // metadata.json never gets one: JSON.parse rejects a leading U+FEFF, so a mark there
  // would break every reader of the file to help a spreadsheet that will not open it.
  if (bom) contents = UTF8_BOM + contents;
  try {
    // The sidecars are built in memory before being written, so compressing them in memory
    // costs nothing extra. Only the signal tables are large enough to need a stream.
    await writeFile(path.join(outputDir, name), gzip ? gzipSync(contents) : contents, gzip ? undefined : 'utf8');
  } catch (cause) {
    // The same as the signal tables above; see the note there.
    throw new ConversionError(
      'WRITE_FAILED',
      `Writing "${name}" to "${outputDir}" failed: ${describeFsError(cause)}.`,
      writeHint(cause),
    );
  }
}

async function writeChannelsCsv(
  outputDir: string,
  file: EdfFile,
  plan: ConversionPlan,
  gzip: boolean,
  bom: boolean,
): Promise<WrittenFile> {
  const includedColumns = new Set(plan.groups.flatMap((g) => g.channels.map((c) => c.signal.index)));
  const fileFor = new Map<number, string>();
  for (const group of plan.groups) {
    for (const channel of group.channels) fileFor.set(channel.signal.index, group.fileName);
  }

  /*
    A rate rendered the way the file name beside it in the same row was rendered.

    `sampling_rate_hz` and `output_file` are two cells of one row about one rate, and they were
    written from different renderings of it — the cell from `String`, the name from
    `formatRates` over the whole set. They agree on every rate `String` prints plainly and part
    company on the ones it does not:

        a,0,a,uV,1e-19,100,...,signals_1_000e-19hz.csv,yes

    A reader joining that column against the files on disk finds `1e-19` naming
    `1_000e-19hz`, and `--info` printing a third thing again — except that `--info`'s RATE
    column is `1.000e-19 Hz`, which is the name's own form. channels.csv was the one surface
    rendering the rate for itself.

    Rendered together because that is how the names are: two rates a sixth decimal apart round
    to one string, and `formatRates` widens the whole set when they do, so a per-rate rendering
    would disagree with the names again on exactly those files.
  */
  const groupRates = plan.groups.map((group) => group.rate);
  const rateText = new Map<number, string>(
    formatRates(groupRates).map((text, i) => [groupRates[i] as number, text]),
  );

  const lines = [
    csvRow([
      'column',
      'signal_index',
      'label',
      'unit',
      'sampling_rate_hz',
      'samples_per_record',
      'physical_min',
      'physical_max',
      'digital_min',
      'digital_max',
      'transducer',
      'prefiltering',
      'output_file',
      'converted',
    ]),
  ];

  for (const signal of file.header.signals) {
    if (signal.isAnnotations) continue;
    const column = plan.columnNames.get(signal.index) ?? `signal_${signal.index}`;
    lines.push(
      csvRow([
        column,
        String(signal.index),
        signal.label,
        signal.physicalDimension,
        /*
          Plain decimal, at whatever magnitude the header declares.

          `String` switches to exponent notation below 1e-6 and above 1e21, which `plain`
          exists to undo — annotations.csv's two numeric columns were given it because "an
          EDF+ TAL states its onset as ordinary decimal text, so a file saying `+0.0000001`
          came back as `1e-7` in a column whose every other cell is a plain decimal", and
          `fixed` refuses the same form in signals.csv for the same reason: "a reader parsing
          the column as decimal text has no reason to expect it." channels.csv was the third
          CSV and the one that kept it. A magnetometer calibrated to ±1e-16 T had its
          `physical_min` written `-1e-16` beside a `digital_min` of `-32768`, in the pair of
          columns this documentation points at for recovering the digital codes, over a
          signals.csv writing every one of those values out in full — and a channel calibrated
          to ±100 in the row above it, so the column held both notations at once.
        */
        // Nor the rate, which is rendered above against the names it has to match. Nor
        // samples_per_record, which is a count.
        rateText.get(signal.samplingRate) ?? formatRate(signal.samplingRate),
        String(signal.samplesPerRecord),
        plain(signal.physicalMin),
        plain(signal.physicalMax),
        plain(signal.digitalMin),
        plain(signal.digitalMax),
        signal.transducer,
        signal.prefiltering,
        fileFor.get(signal.index) ?? '',
        includedColumns.has(signal.index) ? 'yes' : 'no',
      ]),
    );
  }

  const name = outputCsvName('channels', gzip);
  await writeOutputFile(outputDir, name, lines.join('\n') + '\n', gzip, bom);
  return { name, rows: lines.length - 1 };
}

/*
  The bounds an annotation is filtered against: what the caller actually asked for, not the
  window that survived being clamped to the recording.

  Filtering by the resolved window meant an unbounded request still lost events. `--end 999h`
  on a three-second file clamps to 3, and `--start 0` gets its end from the recording, so
  both filtered to [0, 3) and dropped the markers at 3.0 and 3.5 that a bare invocation
  keeps — asking for more of a recording returned less of it.

  An end the caller did not give is unbounded, not "the end of the data": EDF+ lets an
  annotation sit past the last sample, which is exactly where an end-of-recording marker is.

  `--duration` is measured from wherever the conversion actually starts. Anchoring it at 0
  instead read the same absent `--start` two ways in two adjacent lines, and on a recording
  that does not begin at zero the two windows did not even overlap: an EDF+D file whose first
  record sits at 30 s converted its samples from [30, 35) while filtering annotations against
  (-inf, 5), so every event inside the converted window was dropped and `annotations.csv` came
  back empty. `resolveRange` has always defaulted the same missing start to the earliest
  record, which is where this now takes it from.
*/
/**
 * What the durations in the events that will actually be written look like.
 *
 * These two warnings were raised from the file-wide counts the decoder accumulates, while
 * `annotations.csv` is filtered to the requested window. A conversion of one second of a
 * recording therefore warned that "1 annotation states a duration that is not a number, so
 * its duration_s cell is empty" about an event two seconds outside it — naming a cell that is
 * not in the output — and `--strict` failed the run for it. There is no such value, no such
 * cell, and no such row.
 *
 * Taken from the events themselves, after the same filter the writer applies, so the count
 * and the sentence describe the same rows. An unreadable duration is carried on the event
 * because `duration: null` cannot say whether the file gave one; a negative duration needs no
 * flag, since the value is right there.
 */
export function durationDiagnostics(
  annotations: readonly Annotation[],
  window: { from: number; to: number },
  /*
    And the name the run writes that file under, which these sentences did not have.

    Both hints send the reader to `annotations.csv` to look at the rows they are about, and a
    `--gzip` run writes `annotations.csv.gz`. `emptyAnnotations` two functions down takes this
    for the same reason and has since 0.8.48 — "`--info` named `annotations.csv` for a run that
    wrote `annotations.csv.gz`" — and the four beside it were left naming a file that is not in
    the directory.
  */
  gzip = false,
): Diagnostic[] {
  const written = annotations.filter((a) => a.onset >= window.from && a.onset < window.to);
  const eventsFile = outputCsvName('annotations', gzip);
  const diagnostics: Diagnostic[] = [];

  const negative = written.filter((a) => a.duration !== null && a.duration < 0).length;
  if (negative > 0) {
    const one = negative === 1;
    diagnostics.push({
      code: 'ANNOTATION_DECODE_FAILED',
      severity: 'warning',
      message:
        `${counted(negative, 'annotation')} state${one ? 's' : ''} a duration below zero, ` +
        `which is not a length of time.`,
      hint:
        `The value is written to ${eventsFile} as the file gave it. Adding it to onset_s ` +
        'ends the event before it starts, so check these rows before using the durations.',
    });
  }

  const unreadable = written.filter((a) => a.durationUnreadable === true).length;
  if (unreadable > 0) {
    const one = unreadable === 1;
    diagnostics.push({
      code: 'ANNOTATION_DECODE_FAILED',
      severity: 'warning',
      message:
        `${counted(unreadable, 'annotation')} state${one ? 's' : ''} a duration that is ` +
        `not a number, so ${one ? 'its' : 'their'} duration_s cell is empty.`,
      hint:
        'The onset and the description were read normally. An empty duration_s otherwise ' +
        'means the file stated no duration, so these rows cannot be told apart from those.',
    });
  }

  return diagnostics;
}

/**
 * What the descriptions in the events that will actually be written look like.
 *
 * EDF's four free-text header fields have had two warnings about where they land since they
 * were written: `FORMULA_LABEL` for text a spreadsheet runs instead of reading, and
 * `NONPRINTABLE_LABEL` for bytes that drive a terminal. Both say the same thing about the
 * remedy — the text is written exactly as the file has it, because rewriting it would mean
 * the CSV no longer says what the recording says — and both exist so that the tool is not
 * silent about where it goes.
 *
 * `annotations.csv`'s `description` column is the same kind of text, out of the same file,
 * into the same spreadsheet, and nothing was said about it at all. An event described
 * `=HYPERLINK("http://…","Sleep stage W")` was written verbatim, exit 0, no warning, and
 * opens as a live link nobody in the reading chain wrote; one carrying `\x1b[31m` turns the
 * terminal red on `cat annotations.csv`. It is the more likely of the two to happen by
 * accident, since a description is typed by a person at a scoring station while a channel
 * label is written once by the recorder.
 *
 * It is also the only free text in the output that can carry a character above U+00FF —
 * header text is decoded latin1, so every byte of it becomes a code point below U+0100, and
 * a bidirectional override cannot reach a label. It can reach a description, which is UTF-8.
 *
 * Counted rather than raised per event, unlike the header's four fields: a night's scoring is
 * thousands of events, and a warning each is not a report. The count is of the rows that
 * reach `annotations.csv`, after the same window filter the writer applies, for the reason
 * `durationDiagnostics` beside it gives.
 */
export function descriptionDiagnostics(
  annotations: readonly Annotation[],
  window: { from: number; to: number },
  /** The name the run writes the event list under; see `durationDiagnostics`. */
  gzip = false,
): Diagnostic[] {
  const written = annotations.filter((a) => a.onset >= window.from && a.onset < window.to);
  const eventsFile = outputCsvName('annotations', gzip);
  const diagnostics: Diagnostic[] = [];

  const formulaic = written.filter((a) => startsFormula(a.text));
  if (formulaic.length > 0) {
    const one = formulaic.length === 1;
    const shown = [...new Set(formulaic.map((a) => a.text[0] as string))].join(', ');
    diagnostics.push({
      code: 'FORMULA_LABEL',
      severity: 'warning',
      message:
        `${counted(formulaic.length, 'annotation')} ${one ? 'has a description' : 'have descriptions'} ` +
        `starting with ${shown}, which Excel, LibreOffice and Google Sheets read as the start ` +
        `of a formula rather than as text.`,
      hint:
        `The text is written to ${eventsFile} exactly as the file has it, so the cell is ` +
        'what the recording says. Open the CSV with pandas or R, or import it into the ' +
        'spreadsheet as text, if you do not want it evaluated.',
    });
  }

  const marked = written.filter((a) => unprintableIn(a.text).length > 0);
  if (marked.length > 0) {
    const one = marked.length === 1;
    const shown = [...new Set(marked.flatMap((a) => unprintableIn(a.text)))]
      .map(escapeCharacter)
      .join(', ');
    diagnostics.push({
      code: 'NONPRINTABLE_LABEL',
      severity: 'warning',
      message:
        `${counted(marked.length, 'annotation')} ${one ? 'has a description' : 'have descriptions'} ` +
        `carrying text a terminal does not print as itself (${shown}), written to ` +
        `${eventsFile} exactly as the file has ${one ? 'it' : 'them'}.`,
      hint:
        'A control byte can drive the terminal and a bidirectional override reverses what ' +
        'follows it, so read the file with pandas or R rather than with cat. The cell is ' +
        'what the recording says either way.',
    });
  }

  return diagnostics;
}

/**
 * The window annotations are filtered by — the bounds as asked for, not as snapped to records.
 *
 * Exported so `--info` can count the events a conversion would write using the same predicate
 * that writes them, rather than a second copy of it.
 */
export function requestedAnnotationWindow(
  options: ConvertOptions,
  recordingStart: number,
): { from: number; to: number } {
  const from = options.start ?? -Infinity;
  const to =
    options.end !== undefined
      ? options.end
      : options.duration !== undefined
        ? (options.start ?? recordingStart) + options.duration
        : Infinity;
  return { from, to };
}

async function writeAnnotationsCsv(
  outputDir: string,
  annotations: readonly Annotation[],
  window: { from: number; to: number },
  gzip: boolean,
  bom: boolean,
): Promise<WrittenFile> {
  const inWindow = annotations
    .filter((a) => a.onset >= window.from && a.onset < window.to)
    .sort((a, b) => a.onset - b.onset || a.recordIndex - b.recordIndex);

  const lines = [csvRow(['onset_s', 'duration_s', 'description', 'record_index'])];
  for (const annotation of inWindow) {
    lines.push(
      csvRow([
        plain(annotation.onset),
        annotation.duration === null ? '' : plain(annotation.duration),
        annotation.text,
        String(annotation.recordIndex),
      ]),
    );
  }

  const name = outputCsvName('annotations', gzip);
  await writeOutputFile(outputDir, name, lines.join('\n') + '\n', gzip, bom);
  return { name, rows: inWindow.length };
}

/**
 * What to try next, chosen from what actually went wrong.
 *
 * Every write failure carried the same advice — "Free up space or choose another destination
 * with --out" — which fits exactly one errno. A directory sitting where signals.csv belongs
 * came back telling the reader to free up disk space, and so did a read-only volume, a
 * permission denial and a path too long for the filesystem. Wrong advice is worse than none:
 * it sends someone to check `df` on a disk that is fine, and the thing that is actually
 * wrong stays unexamined.
 *
 * The errno is the one piece of the failure that names the cause, so it is what picks the
 * sentence. Anything unrecognised keeps the general form rather than guessing.
 */
function writeHint(cause: unknown, toStdout = false): string {
  /*
    The stdout path writes no files, and --out is the flag its user chose not to pass.

    Both halves of this sentence were wrong there: "the files written so far" named files
    that do not exist, and "choose another destination with --out" is advice for a different
    command — the destination is whatever the shell redirected the stream to. Same class as
    the disk-space hint this function replaced, one flag over.
  */
  const preamble = toStdout
    ? 'What reached stdout before it failed is incomplete and should not be used. '
    : 'The files written so far are incomplete and should not be used. ';
  return (
    preamble +
    destinationAdvice(
      cause,
      toStdout ? 'redirect it somewhere else' : 'choose another with --out',
      toStdout ? 'redirect it somewhere shorter' : 'choose a shorter destination with --out',
      'Part of that path no longer exists; make sure nothing is removing it while the ' +
        'conversion runs.',
    )
  );
}

/**
 * The same question asked before anything has been written: the output directory could not be
 * created.
 *
 * Both raisings carried one sentence for every errno — "Check the path exists and that you
 * have permission to write there" — which is the shape `writeHint` above was written to
 * replace, in the words of its own docstring: "Wrong advice is worse than none: it sends
 * someone to check `df` on a disk that is fine, and the thing that is actually wrong stays
 * unexamined." A full disk, a read-only volume and a path past the filesystem's length limit
 * all got advice about a path that exists and a permission that is not the problem, two lines
 * under a message where `describeFsError` had already named the cause exactly.
 *
 * No preamble, because nothing has been written yet — which is the only thing that differs
 * between the two, so the sentences themselves are shared rather than copied.
 */
function createHint(cause: unknown): string {
  return destinationAdvice(
    cause,
    'choose another with --out',
    'choose a shorter destination with --out',
    'Part of that path cannot be created — a symbolic link with nothing behind it, or a ' +
      'mount point that is not mounted; choose another with --out.',
  );
}

/** The sentence an errno earns, with the two phrases that name where "somewhere else" is. */
function destinationAdvice(
  cause: unknown,
  elsewhere: string,
  shorter: string,
  missing: string,
): string {
  const code = (cause as NodeJS.ErrnoException | null)?.code;
  switch (code) {
    case 'ENOSPC':
      return `The destination is out of space; free some up or ${elsewhere}.`;
    case 'EDQUOT':
      return `You are over your disk quota on this filesystem; ${elsewhere}.`;
    case 'EACCES':
    case 'EPERM':
      return `You do not have permission to write there; ${elsewhere}.`;
    case 'EROFS':
      return `That filesystem is mounted read-only; ${elsewhere}.`;
    case 'EISDIR':
      return `A directory is sitting where that file belongs; remove or rename it, or ${elsewhere}.`;
    /*
      The one errno whose sentence is not the same question at both call sites.

      Mid-conversion, a missing component is a directory that went away under a run that had
      already written into it, and "make sure nothing is removing it" is the thing to check.
      Before anything is written it is the opposite: the destination's parents are created
      recursively, so reaching ENOENT there means a component that cannot be created — a
      symbolic link with nothing behind it, or a mount point that is not mounted.

          $ edf2csv rec.edf --out /mnt/archive/out
          error: Cannot create "/mnt/archive/out": part of the path does not exist.
                 Part of that path no longer exists; make sure nothing is removing it
                 while the conversion runs.

      Nothing is removing it and nothing was there. `createHint`'s own docstring says the
      preamble "is the only thing that differs between the two, so the sentences themselves
      are shared rather than copied" — and this is the sentence where that was not so.
    */
    case 'ENOENT':
      return missing;
    case 'ENAMETOOLONG':
      return `That path is longer than the filesystem allows; ${shorter}.`;
    case 'EMFILE':
    case 'ENFILE':
      return `Too many files are open; a recording with many sampling rates opens one output file per rate, so --channels narrows it.`;
    case 'EPIPE':
      return 'Whatever was reading the output closed it before the conversion finished.';
    default:
      return 'Check the destination and run the conversion again.';
  }
}

/**
 * Confirms that everything handed to a file-backed stdout actually arrived.
 *
 * `edf2csv rec.edf --stdout > out.csv` onto a volume that the output very nearly fills lost
 * the tail in silence: 94,977 of 102,400 rows on disk, the file ending mid-row, stderr
 * announcing "Wrote 102,400 rows to stdout." and the process exiting 0. The same recording
 * onto the same volume through `--out` fails correctly, which is what gives it away.
 *
 * POSIX `write` returns a short count rather than an error when the disk fills partway
 * through a single call, and only the NEXT write raises ENOSPC. `--out` always has a next
 * write — channels.csv and metadata.json come after — so it always finds out. `--stdout`
 * has nothing after it, and when fd 1 is a regular file Node's stdout is a SyncWriteStream
 * whose `_write` discards the byte count `writeSync` returns, so nothing is raised at all.
 * No error means no `#failure`, so checking that alone would not have caught this.
 *
 * What can be checked is the descriptor: how much it grew against how much it was given.
 * Only for a regular file — a pipe, a terminal or a socket has no size to compare, and on
 * those a short write cannot go unreported this way. Appending (`>>`) is fine, since the
 * starting size is taken before anything is written.
 *
 * Exported so `--info` can use the same audit a `--stdout` conversion does.
 *
 * `--info` wrote its description with `process.stdout.write` and looked at nothing: redirected
 * into a full filesystem it produced a zero-byte file and exited 0, so `edf2csv rec.edf --info
 * > desc.txt` reported success over nothing at all. A 900-channel recording's description is
 * 58 KB, which is not a size a destination is guaranteed to have.
 */
export function auditStdout(): { count: (bytes: number) => void; verify: () => void } | null {
  let startSize: number;
  try {
    const info = fstatSync(1);
    if (!info.isFile()) return null;
    startSize = info.size;
  } catch {
    // No usable descriptor to audit; the conversion is not the place to complain about it.
    return null;
  }

  let expected = 0;
  return {
    count: (bytes: number): void => {
      expected += bytes;
    },
    verify: (): void => {
      let landed: number;
      try {
        landed = fstatSync(1).size - startSize;
      } catch {
        return;
      }
      if (landed >= expected) return;

      /*
        This audit exists for the failure nothing else reports: a write that is accepted, and
        silently truncated. When the stream itself raised an error there is nothing left for
        it to add, and what it added was a second `error:` line denying the first —

          error: Writing to stdout failed: ENOSPC: no space left on device, write
          error: Writing to stdout failed: 58900 of 58900 bytes did not reach the destination,
                 which stopped accepting them part way through.
                 ... nothing after it raised an error because there was nothing after it.

        one failure, reported twice, the second of the two wrong about it.
      */
      if (process.stdout.errored) return;

      /*
        Nothing landing at all is not a short write, and was described as one.

        `--info > desc.txt` onto a full filesystem left a zero-byte file, and this said the
        destination "stopped accepting them part way through" and that "what is there ends
        mid-row" — of a file with nothing in it and no rows in it.
      */
      if (landed === 0) {
        throw new ConversionError(
          'WRITE_FAILED',
          `Writing to stdout failed: none of the ${counted(expected, 'byte')} reached the destination.`,
          'The destination is almost certainly out of space. Nothing was written, so there ' +
            'is nothing there to discard.',
        );
      }
      throw new ConversionError(
        'WRITE_FAILED',
        // The pair a reader subtracts by eye, so both go through the grouping — the rule
        // 0.8.5 set and reached only as far as the messages already built with `counted`.
        `Writing to stdout failed: ${grouped(expected - landed)} of ` +
          `${counted(expected, 'byte')} did not reach the destination, which stopped ` +
          `accepting them part way through.`,
        'What is there ends mid-row and should not be used. The destination is almost ' +
          'certainly out of space — a short write is how a filesystem reports filling up ' +
          'mid-write, and nothing after it raised an error because there was nothing after it.',
      );
    },
  };
}

/**
 * Why `--stdout` cannot take this recording, or null when it can.
 *
 * Lifted out of the conversion so `--info` can ask the same question. It was not asking:
 * `--info --stdout` on a three-rate recording predicted "Would write 1,155 rows, roughly
 * 22.2 KB" and said the channels "are written to one file per rate" — for a command that
 * refuses to run, writes nothing, and names no files. `--info` exists to say what a
 * conversion will do, and this is one of the things it does.
 *
 * Reported by `--info` as a warning rather than a refusal, for the reason 0.5.51 gives about
 * the destination guards: `--info` writes nothing, so a rule about what the output would be
 * has no business stopping it from describing the recording — and being told the command
 * will not work is exactly what you asked.
 */
export function stdoutRefusal(file: EdfFile, plan: ConversionPlan): ConversionError | null {
  if (!plan.writeSignals) {
    return new ConversionError(
      'UNSUPPORTED_REQUEST',
      '--stdout has no signal data to write because --annotations-only was given.',
      'Drop one of the two flags.',
    );
  }

    /*
      No table at all is its own answer, and neither layout gave it.

      A recording with no signal channels — one holding only EDF+ annotations — produced
      zero rate groups. The wide layout then said "--stdout needs exactly one table, but
      this recording produces 0, one for each sampling rate its channels use ()", with an
      empty parenthetical, advice to narrow to one of no rates, and advice to use
      `--layout long` — which wrote zero bytes to stdout, not even a header row, and exited
      0 while warning that "the signal files hold their headers and no data". There were no
      files and there was no header. The one path that was right about this is
      `--annotations-only`, which refuses outright, and this is the same situation reached
      by a different route.
    */
    if (plan.groups.length === 0) {
      return new ConversionError(
        'UNSUPPORTED_REQUEST',
        file.dataSignals.length === 0
          ? `--stdout has no signal data to write: this recording has no signal channels, ` +
            `only ${file.header.isBdf ? 'BDF+' : 'EDF+'} annotations.`
          : '--stdout has no signal data to write: nothing was selected that carries samples.',
        file.dataSignals.length === 0
          ? /*
           Named the way that conversion would name it, since `--gzip` is already on the
           command line this is refusing. This hint is nothing but a command to run, and the
           command it names writes `annotations.csv.gz` — so a reader who follows it finds
           nothing under the name they were given. Same fault 0.8.48 fixed for the warnings,
           in the one refusal that names a file.
        */
        `Convert to a directory to get its ${outputCsvName('annotations', plan.gzip)}, or ` +
        `drop --stdout.`
          : 'Check --channels and the requested window, or convert to a directory instead.',
      );
    }

    // The long layout is one table whatever the rates are, so it has nothing to refuse.
    if (plan.layout !== 'long' && plan.groups.length !== 1) {
      return new ConversionError(
        'UNSUPPORTED_REQUEST',
        // Naming the rates is the point: the hint says to narrow the selection, and this
        // is what there is to narrow it to. The parenthetical used to repeat the count
        // that had just been given — "produces 3 (its channels use 3 different sampling
        // rates)" — which told nobody anything they could act on.
        `--stdout needs exactly one table, but this recording produces ${grouped(plan.groups.length)}, ` +
          `one for each sampling rate its channels use ` +
          `(${listed(formatRates(plan.groups.map((g) => g.rate)).map((r) => `${r} Hz`))}).`,
        'Narrow it to one rate with --channels, write --layout long to get them all in ' +
          'one table, or convert to a directory instead.',
      );
    }
  return null;
}



/**
 * Asked for signal data and given none to put in a file.
 *
 * Every channel selected carries zero samples per record, so there is no table to make —
 * `edf2csv rec.edf --channels unused` writes channels.csv and metadata.json and no signals.csv
 * at all. The NO_SAMPLES warning explains the channel; nothing explained the missing file, and
 * the documentation says signals.csv is written unless --annotations-only was passed. Someone
 * looking for it should be told where it went.
 *
 * There are two ways to arrive with no groups, and one wording is only true of one of them. A
 * recording that holds nothing but EDF+ annotations has no channel that could have been
 * selected, its channels.csv is a header row and nothing else, and no channel of it carries
 * samples — so "every channel selected", "channels.csv still describes them" and "which
 * channels do carry samples" were three false statements in one warning, printed under a
 * warning that had just said the file has no signal channels.
 *
 * Exported, and worded in the present tense, so `--info` can raise the same one. It was built
 * inline here, which meant the one mode whose purpose is to say what a conversion will do said
 * nothing about the file that conversion would not write: `--info --strict` on a recording of
 * nothing but annotations reported one warning where converting it reported two, and
 * `--info --json` carried the shorter list to whatever reads it. Everything the answer depends
 * on is in the file and the plan, both of which `--info` already has.
 */
export function noSignalFile(file: EdfFile, plan: ConversionPlan): Diagnostic | null {
  if (!plan.writeSignals || plan.groups.length > 0) return null;
  const noChannelsAtAll = file.dataSignals.length === 0;
  return {
    code: 'NO_SAMPLES',
    severity: 'warning',
    message: noChannelsAtAll
      ? 'No signal file is written: there is no signal data in this recording to put in one.'
      : 'No signal file is written: every channel selected carries zero samples per data ' +
        'record, so there is nothing to put in one.',
    // Named as they will be written. `plan.gzip` is on the plan because `--info` named
    // `annotations.csv` for a run that wrote `annotations.csv.gz`; these two sentences are
    // about the same pair of files and were still naming the uncompressed ones.
    hint: noChannelsAtAll
      ? `${outputCsvName('annotations', plan.gzip)} holds whatever events it carries. ` +
        `${outputCsvName('channels', plan.gzip)} lists signal channels, so it has none to list.`
      : // `--info` raises this too, since 0.7.84, and was told to run itself to answer it.
        // Naming where the number lives works in both modes; naming a command works in one.
        'Nothing about them is lost: every channel\'s samples per record is in the channel ' +
        `table --info prints, and in the ${outputCsvName('channels', plan.gzip)} a conversion writes.`,
  };
}

/**
 * The channels whose samples this run actually writes.
 *
 * `plan.columnNames` names every channel of the recording, because the names are derived from
 * the whole file rather than from the selection — so it is not the answer. The groups are:
 * `--channels` builds them out of what was selected, and a channel with no samples per record
 * has none to put in one either.
 */
export function convertedChannels(plan: ConversionPlan): Set<number> {
  return new Set(plan.groups.flatMap((group) => group.channels.map((c) => c.signal.index)));
}

/**
 * The channels whose rate the window holds no samples of, while the run has rows elsewhere.
 *
 * The third way a channel ends up with no cells, after `--annotations-only` and the channels
 * `--channels` leaves out. A rate group is what gets a file, so a window a hundredth of a
 * second wide leaves a 1 Hz channel's file holding its header while a 256 Hz channel in the
 * same recording keeps three rows — and "Its cells carry that value" was as untrue of the
 * first as it is of a channel nothing converts at all.
 *
 * Empty for a run with no rows anywhere, which is `EMPTY_WINDOW`'s case and already answered
 * with its own wording, and for the long layout, where every rate shares one table.
 */
export function channelsWithoutRows(plan: ConversionPlan): Set<number> {
  if (plan.layout === 'long' || plan.estimate.rows === 0) return new Set();
  return new Set(
    plan.groups
      .filter((group) => group.rows === 0)
      .flatMap((group) => group.channels.map((c) => c.signal.index)),
  );
}

/**
 * The signal table an `--annotations-only` run does not write, taken out of the hints about it.
 *
 * That mode writes the event list and nothing else — no signal files at all — and four hints
 * about record timing describe the rows of one. On a recording whose records run backwards it
 * printed both of these over an annotations.csv holding its header and no rows:
 *
 *     warning: This is a discontinuous (EDF+D) recording: its data records are not
 *              contiguous in time.
 *              Each row carries its true recording time, so gaps stay visible instead of
 *              being closed.
 *     warning: 2 data records start earlier than the record before them.
 *              Rows are written in file order, so the time column will not increase
 *              monotonically.
 *
 * There are no rows and no time column. The facts above the hints are about the recording and
 * stay; what changes is the sentence describing what the conversion will do with them, which
 * is the same surgery `withTimingPromiseKept` does to the first of these when the record
 * starts cannot be derived.
 *
 * `writesSignals` rather than the option, because the plan is what settles it.
 */
export function withSignalTableUnwritten(
  diagnostics: readonly Diagnostic[],
  writesSignals: boolean,
  gzip: boolean,
  /*
    And the channels this run leaves out, which is the same question one channel at a time.

    `--annotations-only` writes no cells for any channel; `--channels` writes none for the
    ones it excludes, and the five sentences below are as untrue of those as they are of the
    whole run. `--info` prints `(not selected)` in the OUTPUT column for them and channels.csv
    records them with `converted: no`, and the warning three lines under the table said:

        0  flat      flat  uV  4 Hz  0 to 0     (not selected)
        ...
        warning: Signal 0 ("flat") has digital minimum equal to digital maximum (0), so its
                 values cannot be scaled.
                 Its cells are left empty rather than filled with a value the header cannot
                 justify.

    There are no cells. Under `--strict` that is a failed run over a channel this one does not
    touch. The rewrites are the same rewrites, so the set of them is the argument rather than
    a second copy: empty means no signal table at all, which is what `writesSignals` said.
  */
  converted: ReadonlySet<number> = new Set(),
  /*
    And whether this run writes the file those rewrites send the reader to.

    Every sentence below that takes a cell away offers channels.csv in its place — "channels.csv
    still records the digital range the header gives", "it is named `signal_1` in channels.csv's
    column cell". `--stdout` writes no sidecar at all, and it can be given `--channels`:

        $ edf2csv rec.edf --stdout --channels ECG > rows.csv
        warning: Signal 0 ("flat") has digital minimum equal to digital maximum (0), so its
                 values cannot be scaled.
                 No samples are converted for a channel --channels left out, so there are no
                 cells to leave empty. channels.csv still records the digital range the
                 header gives.

    It does not. The run writes one CSV to the stream and nothing else, so the file offered
    as the place the channel is still described is not written either — which is the same
    thing `withSidecarsNamed` says one pass later about the sentences it knows. These are the
    ones it has never been shown: they do not exist until this pass writes them.
  */
  toStdout = false,
  /*
    And whether the run puts the channel names in rows rather than in a header.

    The three rewrites this pass makes about a channel's *name* were deliberately left out of
    the empty-window case at 0.9.39, on the grounds that a window holding no samples still
    writes the signal file's header row, so every column is there and named exactly as those
    sentences say. True of the wide layout, and of only the wide layout. A long signals.csv
    is `time_s,channel,value`: a channel appears in it as a *value* in the channel column, so
    a run with no rows names no channel anywhere, and all three went on saying it did:

        $ edf2csv gappy.edf --layout long --start 2 --duration 1 --out ./converted
        warning: Signal 0 has no label. It will appear as "signal_0".
        warning: 2 signals share the label "dup" (positions #1, #2).
                 ... a column name each in the wide layout, and a distinct value in the
                 channel column under --layout long.
        warning: No samples fall inside the requested window (2.000s to 3.000s), so the
                 signal file holds its header and no data.

    `channels.csv` still carries the name, which is what the `--annotations-only` rewrites
    have always offered in its place.
  */
  longLayout = false,
  /** The channels this run writes no rows for; see `channelsWithoutRows`. */
  emptiedByWindow: ReadonlySet<number> = new Set(),
): Diagnostic[] {
  /*
    And the third way to arrive with no cells and no rows, which is a window holding none.

    `--annotations-only` writes no signal table and `--channels` writes none for the channels
    it leaves out; a window that falls in a gap, or past the last sample, writes the table and
    nothing in it — "so the signal file holds its header and no data", which is the warning
    sitting in this very list. The sentences above it went on describing the rows and cells of
    a conversion that writes none of either:

        warning: Signal 1 ("dup") has digital minimum equal to digital maximum (0), so its
                 values cannot be scaled.
                 Its cells are left empty rather than filled with a value the header cannot
                 justify.
        warning: 2 data records start earlier than the record before them.
                 Rows are written in file order, so the time column will not increase
                 monotonically.
        warning: No samples fall inside the requested window (2.000s to 3.000s), so the
                 signal files hold their headers and no data.

    Taken from `EMPTY_WINDOW` rather than from a new argument, because that diagnostic is
    already here and is the plan's own answer to the question — raised by `--info` and by a
    conversion alike, so all four callers get it without being asked.

    The rewrites about a channel's *name* are not taken: an empty window still writes the
    header row, so every column is there and named exactly as those three sentences say.
  */
  const NAMES_A_COLUMN = new Set(['EMPTY_LABEL', 'DUPLICATE_LABEL', 'NONPRINTABLE_LABEL']);
  const noRows = writesSignals && diagnostics.some((d) => d.code === 'EMPTY_WINDOW');
  const signalOf = (diagnostic: Diagnostic): number | null => {
    const named = /^Signal (\d+)\b/u.exec(diagnostic.message);
    return named === null ? null : Number(named[1]);
  };
  // A channel whose rate the window emptied. Its own question, because the run does write
  // rows — just none of this channel's — so nothing run-level here changes.
  const emptied = (diagnostic: Diagnostic): boolean => {
    const index = signalOf(diagnostic);
    return index !== null && emptiedByWindow.has(index);
  };
  const skipped = (diagnostic: Diagnostic): boolean => {
    if (!writesSignals) return true;
    if (noRows) return longLayout || !NAMES_A_COLUMN.has(diagnostic.code);
    if (emptied(diagnostic)) return true;
    const index = signalOf(diagnostic);
    return index !== null && !converted.has(index);
  };
  if (writesSignals && diagnostics.every((d) => !skipped(d))) return [...diagnostics];
  /*
    The sidecars named here are named as the run writes them.

    These sentences send the reader to the one file an `--annotations-only` run does write, and
    said `channels.csv` to a `--gzip` run that writes `channels.csv.gz` — five hints pointing at
    a name that is not in the directory, one of them beside the `_ch` collision hint that 0.8.48
    had already taught to say `channels.csv.gz`. So a single run named both spellings, and only
    the second one existed.
  */
  /*
    Named plainly under `--stdout`, where no compressed sidecar is written either.

    `--stdout --gzip` compresses the stream and nothing else, so `channels.csv.gz` is as
    absent as `channels.csv` — and it is the spelling `withSidecarsNamed` already uses one
    pass later when it says none is written.
  */
  const channelsFile = outputCsvName('channels', gzip && !toStdout);
  const annotationsFile = outputCsvName('annotations', gzip);
  /*
    And the event list, which `--stdout --annotations-only` does not write either.

    0.9.29 taught `channelsFile` that `--stdout` writes no sidecar and gave every sentence
    offering one a `--stdout` form; the file the *other* four rewrites offer was left naming
    itself. All four say the events keep their own onsets whatever the records do, and send
    the reader to `annotations.csv` for them — and `--stdout --annotations-only` is refused
    outright, so `--info` is where it is read and nothing is written at all:

        $ edf2csv scored.edf --info --stdout --gzip --annotations-only
        warning: This recording is marked discontinuous (EDF+D): ...
                 --annotations-only writes no signal rows, so nothing here is timed from
                 the records. annotations.csv.gz carries each event's own onset, and the
                 record it came from in record_index.
        warning: --stdout would refuse this run: has no signal data to write because
                 --annotations-only was given.

    `annotations.csv.gz` under a flag pair that writes no file under either name. Named as
    the conversion that does write it, which is the advice the refusal under it gives.
  */
  const eventsInstead =
    toStdout && !writesSignals
      ? `--stdout writes no ${outputCsvName('annotations', false)} either, so nothing is ` +
        `written at all; a conversion to a directory carries each event's own onset and the ` +
        `record it came from in record_index.`
      : null;
  /*
    What channels.csv still holds about the channel, or that there is no channels.csv.

    Worded the way the sidecar pass words the hints it rewrites — the file is named, so the
    reader knows what they are missing, and the command that produces it is named too.
  */
  const records = (what: string): string =>
    toStdout
      ? `--stdout writes no ${channelsFile}; a conversion to a directory records ${what}.`
      : `${channelsFile} still records ${what}.`;
  return diagnostics.map((diagnostic) => {
    if (!skipped(diagnostic)) return diagnostic;
    // The reason the cells are missing, which is the only part that differs between the three.
    const because = !writesSignals
      ? 'with --annotations-only'
      : noRows
        ? 'for a window that selects none'
        : emptied(diagnostic)
          ? "for a window holding none of this channel's samples"
          : 'for a channel --channels left out';
    // The run-level sentences below are reached when the run writes no signal rows at all,
    // which a selection does not cause: it leaves the rows alone, so nothing it excludes
    // changes what the time column says.
    const writes = noRows
      ? 'The requested window holds no samples'
      : '--annotations-only writes no signal rows';

    /*
      And the three calibration warnings, whose hints are about cells.

      Each describes what a conversion does with a channel whose header cannot map cleanly —
      leaves the cell empty, fills it with the one value the mapping has, keeps the inversion —
      and `--annotations-only` converts no samples, so none of it happens:

          warning: Signal 0 ("flat") has digital minimum equal to digital maximum (0), so its
                   values cannot be scaled.
                   Its cells are left empty rather than filled with a value the header cannot
                   justify.

      What is wrong with the header is still worth saying, and channels.csv — which that run
      does write — still carries the calibration. One message also ends in the conversion:
      "so every sample converts to the same value", over a run converting none.
    */
    /*
      And the two that say where a channel's name lands, which is signals.csv in both layouts
      and neither of them here.

      `--annotations-only` writes no signal table at all, so the name reaches exactly one
      place: channels.csv's `column` cell, which that run does write — control bytes, `_ch`
      suffix and all. Both sentences sent the reader to a file that is not there:

          warning: Signal 0's label and unit contain 2 control characters (\x1b), which will
                   appear as the channel's name in signals.csv and in channels.csv's unit
                   cell in any conversion that writes one ...
          warning: 2 signals share the label "T8-P8" (positions #0, #1).
                   Their names are suffixed with the signal number so they stay
                   distinguishable: a column name each in the wide layout, and a distinct
                   value in the channel column under --layout long.

      Neither layout happens. The second sentence offers a choice between two files that are
      not written.
    */
    if (diagnostic.code === 'NONPRINTABLE_LABEL') {
      return {
        ...diagnostic,
        message: diagnostic.message
          /*
            The hedge goes where the file goes, so it is taken off wherever the header put it
            and folded into the clause below. A run writing into a directory does write
            channels.csv, and the qualification is noise there; a `--stdout` run writes none,
            which is the case the header's own sentence has been qualified for since 0.8.52.
          */
          .replace(' in any conversion that writes one', '')
          .replace(
            "as the channel's name in signals.csv",
            `as the channel's name in ${channelsFile}'s column cell` +
              `${toStdout ? ' in any conversion that writes one' : ''}`,
          )
          // Matched on the name the header diagnostic used, which the line above has not
          // touched: it renames the first mention, and this collapses the second.
          .replace("and in channels.csv's", 'and in its'),
      };
    }
    /*
      And the channel that lost its own name, whose sentence ends in a column.

      plan.ts writes "so its column is `X`" for a run that writes a signal table and "so it is
      named `X` in channels.csv's column cell" for one that does not — decided by the run,
      because that is all it knows there. A selection decides it per channel: the rename still
      happens, since the names are derived from the whole file so that they agree with
      channels.csv and across runs, and the column it is named for is one this run has not got.
    */
    // Either noun the plan may have written: a column of the wide table, or the channel
    // column a long one names it in. Both are places this run has no rows in.
    if (
      diagnostic.code === 'DUPLICATE_LABEL' &&
      / so (?:its column is "|it is named "[^"]*" in the channel column)/u.test(diagnostic.message)
    ) {
      return {
        ...diagnostic,
        message: diagnostic.message.replace(
          / so (?:its column is ("[^"]*")|it is named ("[^"]*") in the channel column)\.$/u,
          // Whichever of the two groups matched carries the name; the other is undefined,
          // and `$1` would have written an empty pair of quotes for the long-layout form.
          (_all, wide: string | undefined, long: string | undefined) =>
            ` so it is named ${wide ?? long} in ${channelsFile}'s column cell` +
            `${toStdout ? ' in any conversion that writes one' : ''}.`,
        ),
      };
    }
    /*
      And the same sentence when the plan wrote it in this form to begin with.

      plan.ts decides between three nouns from the options it has — a column, the channel
      column of a long table, or a cell of channels.csv when no signal table is written at
      all — so under `--annotations-only` the sentence arrives already naming the cell, and
      the rewrite above, which matches the wide-layout form, never sees it. Which left the
      one form this pass could not touch as the one form carrying neither of the two things
      it knows: the hedge, and what `--stdout` calls that file.

          $ edf2csv montage.edf --info --stdout --gzip --annotations-only
          warning: Signal 2 is labelled "T8_ch0", ... so it is named "T8_ch0_ch2" in
                   channels.csv.gz's column cell.
                   Channel names are unique, and --stdout writes no channels.csv to look
                   this channel up in by its signal_index — convert to a directory for that.

      One warning, one file, two spellings of it, and neither written by that run.
    */
    if (diagnostic.code === 'DUPLICATE_LABEL' && /'s column cell\.$/u.test(diagnostic.message)) {
      return {
        ...diagnostic,
        message: diagnostic.message.replace(
          / in [\w.]+'s column cell\.$/u,
          ` in ${channelsFile}'s column cell` +
            `${toStdout ? ' in any conversion that writes one' : ''}.`,
        ),
      };
    }
    if (
      diagnostic.code === 'DUPLICATE_LABEL' &&
      diagnostic.hint?.startsWith('Their names are suffixed')
    ) {
      return {
        ...diagnostic,
        hint:
          'Their names are suffixed with the signal number so they stay distinguishable. ' +
          (writesSignals
            ? noRows
              ? toStdout
                ? `The requested window holds no samples, and --stdout writes no ` +
                  `${channelsFile} for the suffixed names to appear in — convert to a ` +
                  `directory for that.`
                : `The requested window holds no samples, so the suffixed names appear only ` +
                  `in ${channelsFile}'s column cells.`
              : toStdout
                ? `--channels left this channel out, and --stdout writes no ${channelsFile} for ` +
                  `its suffixed name to appear in — convert to a directory for that.`
                : `--channels left this channel out, so its suffixed name appears in ` +
                  `${channelsFile}'s column cell and nowhere else.`
            : toStdout
              ? // The one of the three this pass was extended without at 0.9.29. `--stdout`
                // writes no channels.csv either, and the report saying this goes on to say
                // so two warnings later — "--stdout writes no channels.csv to look this
                // channel up in" — so one run carried both sentences about one file.
                `--annotations-only writes no signal table, and --stdout writes no ` +
                `${channelsFile} for the suffixed names to appear in — convert to a ` +
                `directory for that.`
              : '--annotations-only writes no signal table, so the suffixed names appear ' +
                `only in ${channelsFile}'s column cells.`),
      };
    }
    /*
      And the channel with no label at all, whose sentence ends in a column too.

      `EMPTY_LABEL` says where the name this tool invents for it lands, and both of its forms
      land it in a signal table: "It will appear as `signal_1`", and, where some other channel
      really carries that label, "so both columns are suffixed with their position instead".
      A run writing no signal table puts the name in one place, which is the cell the two
      rewrites above already name.
    */
    if (diagnostic.code === 'EMPTY_LABEL') {
      // Hedged under `--stdout`, where the cell is in a file that run does not write — the
      // wording 0.8.52 gave the same clause in NONPRINTABLE_LABEL, for the same reason.
      // Spelled out as its own string rather than by appending an s to the singular, which
      // reads as a number of seconds to the check that keeps those going through
      // `plainSeconds` — and does so whether it is in code or in a comment about it.
      const hedge = toStdout ? ' in any conversion that writes one' : '';
      const cell = `${channelsFile}'s column cell${hedge}`;
      const cells = `${channelsFile}'s column cells${hedge}`;
      return {
        ...diagnostic,
        message: diagnostic.message
          .replace(/ It will appear as ("[^"]*")\.$/u, ` It is named $1 in ${cell}.`)
          .replace(
            'so both columns are suffixed with their position instead.',
            `so both are suffixed with their position in ${cells} instead.`,
          ),
      };
    }
    /*
      And the fourth calibration warning, which the three above were rewritten without.

      `UNUSABLE_PHYSICAL_RANGE` ends in the same sentence they do — "Its cells are left empty
      rather than filled with a value the header cannot justify" — and needs a header no
      fixture has, a span that underflows to zero or one too large to represent, which is how
      it stayed out of the sweep that found the others.
    */
    if (diagnostic.code === 'UNUSABLE_PHYSICAL_RANGE') {
      return {
        ...diagnostic,
        hint:
          `No samples are converted ${because}, so there are no cells to leave empty. ` +
          `${records('the physical range the header gives')}`,
      };
    }
    /*
      And the one warning here that is about the printed text of a sample rather than a cell.

      `VALUE_RESOLUTION` says a channel's step is below any precision `toFixed` can print, so
      consecutive samples land on the same text — and reassures the reader that nothing is
      lost from the data: "Every sample is written, in order". A window holding none writes no
      samples at all, which the warning under it says in as many words:

          warning: fine steps by less than any number of decimals this can print, so some
                   consecutive samples round to the same value in signals.csv.
                   Every sample is written, in order, and the physical values are computed
                   at full precision either way.
          warning: No samples fall inside the requested window (2.000s to 3.000s), so the
                   signal file holds its header and no data.

      plan.ts already gives this reassurance its own branch where the rate overflows to
      Infinity, on the stated grounds that "no rows are written at all, so 'Every sample is
      written, in order' would be the third untrue sentence". This is that same sentence,
      reached by the other route. `--annotations-only` and `--channels` never see it: the
      warning is raised per rate group from the channels the plan converts, so a run that
      converts none raises none.
    */
    /*
      And the rate warning, whose long-layout half is a sentence about rows.

      `MIXED_SAMPLING_RATES` explains why the output is shaped the way it is, and it says so
      differently for each layout: the wide one writes "one file per rate", which a window
      holding no samples still writes — with a header and nothing in it, but written. The long
      one writes "They share one table, each row carrying its own time", and there are no rows
      to carry anything. Only the clause about the rows moves; the rates and the promise not
      to resample are facts about the recording.
    */
    if (
      noRows &&
      diagnostic.code === 'MIXED_SAMPLING_RATES' &&
      diagnostic.hint?.startsWith('They share one table') === true
    ) {
      return {
        ...diagnostic,
        hint:
          'They share one table, which the requested window leaves holding its header and ' +
          'no rows. No channel is resampled.',
      };
    }
    if (diagnostic.code === 'VALUE_RESOLUTION') {
      return {
        ...diagnostic,
        message: diagnostic.message.replace(
          ' so some consecutive samples round to the same value in',
          ' so some consecutive samples would round to the same value in',
        ),
        hint:
          `No samples are converted ${because}, so none is rounded at all. The physical ` +
          'values are computed at full precision either way, and what a printed one loses ' +
          'is only in its text.',
      };
    }
    if (diagnostic.code === 'DEGENERATE_DIGITAL_RANGE') {
      return {
        ...diagnostic,
        hint:
          `No samples are converted ${because}, so there are no cells to leave empty. ` +
          `${records('the digital range the header gives')}`,
      };
    }
    if (diagnostic.code === 'DEGENERATE_PHYSICAL_RANGE') {
      return {
        ...diagnostic,
        message: diagnostic.message.replace(
          'so every sample converts to the same value.',
          'so every sample would convert to the same value.',
        ),
        hint:
          `No samples are converted ${because}. ${records('the calibration, one point wide')}`,
      };
    }
    if (diagnostic.code === 'INVERTED_PHYSICAL_RANGE') {
      return {
        ...diagnostic,
        hint:
          `No samples are converted ${because}. ` +
          `${records('the physical minimum and maximum in the order the header gives them, inversion included')}`,
      };
    }
    /*
      And the record this one could not place, which is timed from its neighbours in a signal
      table and nowhere else. Its events are exported whatever its position is: an onset is
      read off the event, not off the record it sits in, and `record_index` still names that
      record.
    */
    if (
      diagnostic.hint?.startsWith('That record is timed as if') === true ||
      diagnostic.hint?.startsWith('Those records are timed as if') === true
    ) {
      const one = diagnostic.hint.startsWith('That record');
      return {
        ...diagnostic,
        hint:
          `${writes}, so nothing is timed from ${one ? 'that record' : 'those records'}. ` +
          (eventsInstead ??
            `The events in ${one ? 'it' : 'them'} carry their own onsets, and ` +
              `${annotationsFile}'s record_index still names ${one ? 'it' : 'them'}.`),
      };
    }
    if (diagnostic.code !== 'DISCONTINUOUS' || diagnostic.hint === undefined) return diagnostic;
    if (diagnostic.hint.startsWith('Each row carries its true recording time')) {
      return {
        ...diagnostic,
        hint:
          `${writes}, so nothing here is timed from the records. ` +
          (eventsInstead ??
            `${annotationsFile} carries each event's own onset, and the record it ` +
              'came from in record_index.'),
      };
    }
    if (diagnostic.hint.startsWith('Rows are written in file order')) {
      return {
        ...diagnostic,
        hint:
          `${writes}, so no time column is affected. ` +
          (eventsInstead ??
            `${annotationsFile}'s record_index still names the record each event came from.`),
      };
    }
    if (diagnostic.hint.startsWith('Sample times are written from zero')) {
      return {
        ...diagnostic,
        hint: `${writes}, so no sample times are written at all.`,
      };
    }
    /*
      And the three that say the times are written as if the records were contiguous, which
      this pass had not been shown.

      Those are the answers for a file whose record positions are not recorded, or are
      contradicted by its own `EDF+C` marker — and they describe a time column. An
      `--annotations-only` run writes none, so all three promise something about rows it does
      not write:

          warning: This file is marked continuous (EDF+C), but 1 of its 3 data records says
                   it starts somewhere other than where continuity puts it.
                   Times are written as if the records were contiguous, which is what EDF+C
                   means. ...
          warning: This file is marked discontinuous but has no annotation channel, so where
                   its records sit in time is not recorded anywhere.
                   Times are written as if the records were contiguous. Any gaps are lost.

      over an annotations.csv holding its header and no rows. The three sentences above this
      one were amended for exactly this at 0.8.x; these were the DISCONTINUOUS hints that do
      not begin with any of their phrases.

      What the header says about itself is still worth saying, so the messages stay. Only the
      clause about what a conversion makes of it was false, and what is true instead is that
      the events carry their own onsets whatever the records do.
    */
    if (diagnostic.hint.startsWith('Times are written as if the records were contiguous')) {
      return {
        ...diagnostic,
        hint:
          `${writes}, so no times are written from the records at all. ` +
          (eventsInstead ??
            `${annotationsFile} carries each event's own onset, which the ` +
              'records do not decide.'),
      };
    }
    // The marker warning's hint, which `withTimingPromiseKept` has already rewritten once and
    // which points at the warning under it. That pointer is still right and is what separates
    // the two, so it is the clause about the time column that goes and not the sentence.
    if (diagnostic.hint.startsWith('Where its records sit in time is not recorded in this file')) {
      return {
        ...diagnostic,
        hint:
          'Where its records sit in time is not recorded in this file, and ' +
          `${writes} to time from them anyway — see the ` +
          'warning below.',
      };
    }
    return diagnostic;
  });
}

/**
 * The sidecar files a `--stdout` run does not write, taken out of the sentences about them.
 *
 * `--stdout` puts one table on the stream and writes nothing else — "No sidecar files are
 * written", as `ConvertOptions` puts it. Two diagnostics raised before the destination is
 * known end by pointing at one of those files:
 *
 *     warning: The header's start date and time ("XX.XX.XX" and "YY.YY.YY") are not a date
 *              and a time, so the recording has no start instant.
 *              ... and metadata.json records start_datetime_local as null.
 *
 *     warning: This recording's timekeeping annotations place it 1e17s from its own start
 *              date ...
 *              ... Add the onsets in annotations.csv to recover absolute times.
 *
 * Neither file exists after such a run. The second is advice a reader can follow into an
 * empty directory — there is no directory.
 *
 * Amended where the answer is, the same way `withTimingPromiseKept` rewrites a `DISCONTINUOUS`
 * hint the parser could not have known was false, and `withoutFileRateWarning` drops a header
 * diagnostic the plan supersedes. A conversion to a directory keeps every word.
 */
export function withSidecarsNamed(
  diagnostics: readonly Diagnostic[],
  { toStdout, gzip }: { toStdout: boolean; gzip: boolean },
): Diagnostic[] {
  if (!toStdout && !gzip) return [...diagnostics];
  const channels = outputCsvName('channels', gzip);
  const annotations = outputCsvName('annotations', gzip);
  return diagnostics.map((diagnostic) => {
    /*
      The last message of this kind, and the one that names its files mid-sentence.

      `NONPRINTABLE_LABEL` says where an invisible byte lands — the channel's name in
      signals.csv, a cell of channels.csv — and `--gzip` writes neither of those names. It was
      left out of 0.8.48 because the file is named inside a sentence assembled from which of
      the four header fields carry bytes, and out of 0.8.54 for the same reason. Renaming is
      all it needs, once the sentence is settled: this runs after `withSignalTableUnwritten`
      has had its say about the layout, so whichever file the sentence ended up naming is the
      one that gets the suffix.
    */
    /*
      And the same sentence under `--stdout`, where the signal table has no name at all.

      The `channels.csv` half of it has been hedged since 0.8.52 — "in the channels.csv of any
      conversion that writes one", which is true of a run that writes none. The half naming
      `signals.csv` was not, and `--stdout` is the mode this warning matters most in: it is
      the one that really does print the CSV to a terminal, which is what the hint two lines
      under it warns about.

          $ edf2csv control-labels.edf --stdout | less
          warning: Signal 1's label contains 1 control character (\x07), which will appear as
                   the channel's name in signals.csv, exactly as the header has it.

      There is no signals.csv. The byte is in the header row going past on the stream, which
      is where the reader is looking.
    */
    if ((gzip || toStdout) && diagnostic.code === 'NONPRINTABLE_LABEL') {
      return {
        ...diagnostic,
        message: diagnostic.message
          // Past a name the pass above already suffixed: `channels.csv.gz` contains
          // `channels.csv`, and a second rename made it `channels.csv.gz.gz`.
          .replace(
            /\bsignals\.csv\b(?!\.gz)/gu,
            toStdout ? 'the CSV on stdout' : outputCsvName('signals', true),
          )
          .replace(/\bchannels\.csv\b(?!\.gz)/gu, channels),
      };
    }
    /*
      The header's own `NO_SAMPLES`, which names the file that describes the channel it is
      about — and under `--stdout` there is no such file, so the reassurance is empty:

          $ edf2csv one-empty-channel.edf --stdout > rows.csv
          warning: Signal 1 ("unused") carries no samples at all (0 per data record).
                   It is described in channels.csv but left out of the converted data.

      Under `--gzip` the file is there under another name. Both are settled here.
    */
    if (diagnostic.code === 'NO_SAMPLES' && diagnostic.hint?.startsWith('It is described in')) {
      return {
        ...diagnostic,
        hint: toStdout
          ? 'It is left out of the converted data, and --stdout writes no channels.csv to ' +
            'describe it in — convert to a directory for that.'
          : `It is described in ${channels} but left out of the converted data.`,
      };
    }
    /*
      And the other `NO_SAMPLES`, whose whole hint is the two files written instead.

      `noSignalFile` says where the signal data went for a recording that has none, and for
      the one shape of that — a file holding nothing but EDF+ annotations — the reassurance
      is a list of what the run does write. `--stdout` writes neither, and refuses the run
      outright, which the warning under it then says:

          $ edf2csv scoring.edf --info --stdout
          warning: No signal file is written: there is no signal data in this recording
                   to put in one.
                   annotations.csv holds whatever events it carries. channels.csv lists
                   signal channels, so it has none to list.
          warning: --stdout would refuse this run: has no signal data to write: this
                   recording has no signal channels, only EDF+ annotations.
                   Convert to a directory to get its annotations.csv, or drop --stdout.

      Two consecutive warnings, the first offering the file the second says to convert to a
      directory to get. Named as the conversion that does write them, which is that advice.
    */
    /*
      And the empty event list, whose message ends in the file that holds nothing.

      `emptyAnnotations` is only raised where no signal table is written, which under
      `--stdout` means `--annotations-only` — a pair this tool refuses, so `--info` is where
      it is read. Both forms of the message name the file, and `--gzip` named the compressed
      one: "annotations.csv.gz holds its header and no rows", of a run that writes no file
      under either name, two lines above the warning saying the run would be refused.

      The message stays a statement about the recording — its channel really does carry no
      events, or none inside the window — and what moves is the file: the one a conversion to
      a directory would write, which is where the refusal under it sends the reader.
    */
    if (toStdout && diagnostic.code === 'NO_ANNOTATIONS' && / holds its header and no rows\.$/u.test(diagnostic.message)) {
      return {
        ...diagnostic,
        message: diagnostic.message.replace(
          /, so [\w.]+ holds its header and no rows\.$/u,
          `, and --stdout writes no ${outputCsvName('annotations', false)}; a conversion ` +
            `to a directory writes one holding its header and no rows.`,
        ),
      };
    }
    if (
      toStdout &&
      diagnostic.code === 'NO_SAMPLES' &&
      diagnostic.hint?.includes('holds whatever events it carries') === true
    ) {
      return {
        ...diagnostic,
        hint:
          `--stdout writes neither: a conversion to a directory gets ${annotations} with ` +
          `whatever events this recording carries, and a ${channels} with no signal ` +
          'channels to list.',
      };
    }
    /*
      The collision hint, whose whole advice is a file to look the channel up in.

      A duplicate label, or a label that collides with another channel's `_ch` suffix, renames
      the column — and the sentence saying so ends by sending the reader to channels.csv for
      the channel's `signal_index`, since the renamed name is the only one in the table:

          $ edf2csv montage.edf --stdout > rows.csv
          warning: Signal 2 is labelled "T8_ch0", which is also the column name another
                   channel's "_ch" suffix produces, so its column is "T8_ch0_ch2".
                   Column names are unique; look this channel up in channels.csv by its
                   signal_index.

      There is no channels.csv, and the renamed column is sitting in the header row on stdout
      with nothing to map it back. Under `--gzip` it was worse: `channels.csv.gz`, a
      compressed file no `--stdout` run writes either.

      Answered the way the two hints above answer it — the file is named, so that the reader
      knows what they are missing, and the command that produces it is named too.
    */
    if (toStdout && diagnostic.code === 'DUPLICATE_LABEL' && diagnostic.hint?.includes('look this channel up in')) {
      // "Channel names" under --layout long, where the name lands in the channel column;
      // "Column names" in the wide layout. Both are true of the stream, so the half of the
      // sentence that is about the rename is kept as the plan wrote it.
      const unique = diagnostic.hint.startsWith('Channel names') ? 'Channel names' : 'Column names';
      return {
        ...diagnostic,
        hint:
          `${unique} are unique, and --stdout writes no channels.csv to look this channel ` +
          `up in by its signal_index — convert to a directory for that.`,
      };
    }
    if (
      toStdout &&
      diagnostic.code === 'START_TIME_UNREADABLE' &&
      diagnostic.hint?.includes('metadata.json records')
    ) {
      return {
        ...diagnostic,
        hint:
          'time_s is unaffected — it counts from the start of the recording either way. What ' +
          'cannot be done is turning it into a wall-clock instant, and --stdout writes no ' +
          'metadata.json to record start_datetime_local as null in.',
      };
    }
    if (
      diagnostic.code === 'DISCONTINUOUS' &&
      diagnostic.hint?.includes('onsets in annotations.csv')
    ) {
      return {
        ...diagnostic,
        hint: toStdout
          ? 'Sample times are written from zero instead, so every row is present and the ' +
            "column increases. The onsets that recover it are the events' own, in the " +
            'annotation channel; --stdout writes no annotations.csv to read them from, and ' +
            'a recording carrying no events has none of them either.'
          : 'Sample times are written from zero instead, so every row is present and the ' +
            `column increases. An event's own onset is on the original clock, so the onsets ` +
            `in ${annotations} recover it for a recording that carries events — the ` +
            'timekeeping entries that place the records are never exported.',
      };
    }
    return diagnostic;
  });
}

/**
 * `--annotations-only` on a recording that has no annotations.
 *
 * Exported and present-tense for the same reason `noSignalFile` above is. `--info` prints an
 * accurate line about it in the report body — "and no annotations.csv either, since this
 * recording has no annotation channel" — and raised nothing, so the one mode whose purpose is
 * to say what a conversion will do carried a shorter warning list than the conversion did:
 * `--info --json --annotations-only` on a plain EDF file listed no warning where converting
 * lists one, and `--info --strict`, which cli-reference.md recommends for screening a folder
 * before converting it, exited 0 where the conversion exits 1. Same defect as the one 0.7.84
 * closed for `NO_SAMPLES`, one flag over; both halves of the answer are in the file and the
 * options, which `--info` already has.
 */
export function noAnnotations(file: EdfFile, options: ConvertOptions): Diagnostic | null {
  if (options.annotationsOnly !== true || file.annotationSignals.length > 0) return null;
  return {
    code: 'NO_ANNOTATIONS',
    severity: 'warning',
    message:
      '--annotations-only was requested but this recording has no annotation channel, ' +
      'so there are no events to export.',
    /*
      The format the file is in, rather than the one most files are in.

      "Plain EDF files carry no annotations" was said to every recording that reaches here,
      and two kinds of file reach it that are not plain EDF. A BioSemi `.bdf` was told about a
      format it is not in — this tool tells EDF and BDF apart everywhere else, down to the
      `BDF Annotations` label the specification gives the channel.

      And a file whose header says `EDF+C` while carrying no annotation channel at all is not
      a file for which having no events is ordinary: EDF+ keeps its events in that channel and
      is required to have one. The sentence read as reassurance about a file that is malformed.
    */
    hint: file.header.isEdfPlus
      ? `The header marks this ${describeFormat(file.header)}, and that format keeps its ` +
        `events in an annotation channel this file does not have. Convert without ` +
        `--annotations-only to get the signals.`
      : `Plain ${file.header.isBdf ? 'BDF' : 'EDF'} files carry no annotations. Convert ` +
        `without --annotations-only to get the signals.`,
  };
}

/**
 * `--annotations-only` that wrote no events, and why.
 *
 * The two causes are told apart because the answers are different. A channel holding nothing
 * but timekeeping entries has nothing to export and never will; a window that excluded every
 * event is a thing the caller can change, and the commonest reason is reading the window off
 * a clock the recording does not use — `--start` and `--end` are on the recording's own, which
 * starts at zero unless `--info` shows a "Timed from" line.
 *
 * `NO_ANNOTATIONS` rather than a new code, since it is the same statement its other raising
 * makes — there are no events to export — about the same flag, and a code is matched on by
 * scripts that should not have to learn a second one for the same fact.
 */
export function emptyAnnotations(
  total: number,
  window: { from: number; to: number },
  gzip = false,
): Diagnostic {
  const windowed = (window.from !== -Infinity || window.to !== Infinity) && total > 0;
  return {
    code: 'NO_ANNOTATIONS',
    severity: 'warning',
    message: windowed
      ? `None of this recording's ${counted(total, 'event')} fall inside the requested ` +
        `window, so ${outputCsvName('annotations', gzip)} holds its header and no rows.`
      : `This recording's annotation channel carries no events, so ` +
        `${outputCsvName('annotations', gzip)} holds its header and no rows.`,
    hint: windowed
      ? /*
          Which is a line --info prints only when there is something to say.

          `Timed from` appears when the first sample is somewhere other than 0s — that is what
          the line is for — so on a recording timed from zero, which is most of them, this sent
          the reader to a line that is not in the report. The one hint whose whole job is "you
          may be using the wrong clock", pointing at nothing, on exactly the files where the
          window and the clock are hardest to tell apart.
        */
        '--start and --end are read on the recording\'s own clock, which starts at 0s unless ' +
        '--info shows a "Timed from" line, and an event is kept when its onset falls inside ' +
        'the window.'
      : 'The channel holds only the timekeeping entries that say where each data record ' +
        'sits, and those are never exported.',
  };
}

/** Raised when the input moved while it was being read. See where it is pushed. */
function inputChanged(hadChecksum: boolean): Diagnostic {
  return {
    code: 'INPUT_CHANGED',
    severity: 'warning',
    message:
      'The input changed while it was being converted, so this output covers the file as ' +
      'it was when the conversion started, not as it is now.',
    hint: hadChecksum
      ? 'No checksum was recorded: the bytes that were converted are no longer there to ' +
        'hash. Convert again once the recording is finished.'
      : 'Convert again once the recording is finished to pick up the rest.',
  };
}

async function writeMetadata(
  outputDir: string,
  inputPath: string,
  file: EdfFile,
  plan: ConversionPlan,
  written: readonly WrittenFile[],
  annotationCount: number,
  /** Hash of the bytes that were converted, or null when it could not be vouched for. */
  checksum: string | null,
  /** Whether the record start times could be read; see withTimingPromiseKept. */
  timedFromRecords: boolean,
  /** Whether each CSV starts with a byte order mark; see the `bom` field below. */
  bom: boolean,
): Promise<void> {
  const { header } = file;

  /*
    The file as it was when it was opened, not as it is now.

    Both of these used to come from re-opening the path once the CSVs were written, which
    describes whatever answers to that name by then rather than what was converted. A
    recording still being written grew from 2,000 records to 3,000 mid-conversion and
    metadata.json recorded `data_records: 2000` — correct, the CSV holds 2,000 — beside the
    byte count and SHA-256 of the 3,000-record file. The two halves of one provenance record
    described two different files, and the checksum covered bytes nobody had converted.
    Replacing the file at that path did the same thing more thoroughly.

    `file.fileSize` is the number every record count and window in this output was derived
    from, and the hash is taken over exactly those bytes through the descriptor already open
    on them, so the record describes one file throughout.
  */
  const metadata = {
    tool: { name: 'edf2csv', version: TOOL_VERSION },
    source: {
      path: path.resolve(inputPath),
      bytes: file.fileSize,
      modified: new Date(file.modifiedAtOpenMs).toISOString(),
      sha256: checksum,
    },
    recording: {
      format: describeFormat(header),
      version: header.version,
      patient_id: header.patientId,
      recording_id: header.recordingId,
      // Zone-less on purpose: EDF records local wall-clock digits and no timezone.
      start_datetime_local: formatWallClock(header.startDateTime),
      start_date_raw: header.startDateRaw,
      start_time_raw: header.startTimeRaw,
      data_records: file.recordCount,
      data_records_declared: header.declaredRecordCount,
      record_duration_seconds: header.recordDuration,
      duration_seconds: file.durationSeconds,
      signal_count: header.signalCount,
      annotation_channels: file.annotationSignals.length,
    },
    conversion: {
      converted_at: new Date().toISOString(),
      start_seconds: plan.range.startSeconds,
      end_seconds: plan.range.endSeconds,
      whole_recording: plan.range.isWholeRecording,
      records_converted: [plan.range.startRecord, plan.range.endRecord],
      annotations_written: annotationCount,
      /*
        Which shape the signal table is in, which nothing recorded.

        A wide `signals.csv` and a long one are different files with different columns, and
        metadata.json described them identically — so a pipeline handed an output directory
        could not tell from the archive which it had. It matters most for `rate_groups` right
        below: in the wide layout those entries are one per file and their `channels` are that
        file's columns, and in the long layout every entry names the one shared table and its
        `channels` are values in that table's `channel` column. Same array, two readings, and
        no way to know which applied.
      */
      layout: plan.layout,
      /*
        The one option whose effect nothing else in this archive shows.

        `layout` is recorded on the stated grounds that "the two have different columns and
        nothing else in the archive distinguishes them". `--gzip` distinguishes itself: the
        names in `files` end `.csv.gz`. `--bom` puts three bytes in front of every CSV and
        leaves no other trace — and it is the option that decides whether reading the table
        back works. `csv.ts` sets out why: pandas strips the mark either engine, and Python's
        own `csv.reader` over a plain `open()` does not, nor does `readFileSync(path,
        'utf8')`, so the first column name comes back as `\ufefftime_s` and a lookup of
        `time_s` misses. A pipeline reading this document to find out how to open the files
        could not find out the one thing that changes the answer.
      */
      bom,
      files: written.map((f) => ({ name: f.name, rows: f.rows })),
      rate_groups: plan.groups.map((g) => ({
        file: g.fileName,
        sampling_rate_hz: g.rate,
        channels: g.channels.map((c) => c.column),
        decimals: g.channels.map((c) => c.decimals),
      })),
    },
    notes: withSidecarsNamed(
      withSignalTableUnwritten(
        [
          ...withTimingPromiseKept(withoutFileRateWarning(file.diagnostics), timedFromRecords),
          ...plan.diagnostics,
        ],
        plan.writeSignals,
        plan.gzip,
        convertedChannels(plan),
        false,
        plan.layout === 'long',
        channelsWithoutRows(plan),
      ),
      // metadata.json is only written into a directory, so this is never the stdout case.
      { toStdout: false, gzip: plan.gzip },
    ).map((d) => ({
      code: d.code,
      severity: d.severity,
      message: d.message,
    })),
  };

  // Escaped like the two --json documents: metadata.json records the input's path, and a
  // `cat` of it should not be able to reorder the terminal. See escapeJsonText.
  const document = escapeJsonText(JSON.stringify(metadata, null, 2));
  await writeOutputFile(outputDir, 'metadata.json', `${document}\n`);
}


